import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import API from '@/lib/api';
import MailContactAutocomplete from '@/components/MailContactAutocomplete';
import MailSidebarAgenda from '@/components/MailSidebarAgenda';
import MailEventProposals from '@/components/MailEventProposals';
import MailOrderProposals from '@/components/MailOrderProposals';
import MailScheduledModal from '@/components/MailScheduledModal';
import MailFollowUpsModal from '@/components/MailFollowUpsModal';
import MailRulesModal from '@/components/MailRulesModal';
import {
  EnvelopeSimple, Tray, PaperPlaneTilt, Trash, Star, Tag, Sparkle,
  ArrowsClockwise, Plus, MagnifyingGlass, X, PaperclipHorizontal, DownloadSimple,
  ArrowBendUpLeft, ArrowBendDoubleUpRight, Gear, FileText, WarningCircle, CheckCircle,
  Prohibit, ShieldCheck, Flag, Clock
} from '@phosphor-icons/react';

const CATEGORY_BADGES = {
  factuur:        { label: 'Factuur',        color: 'bg-red-100 text-red-700 border-red-300' },
  reply:          { label: 'Antwoord',       color: 'bg-blue-100 text-blue-700 border-blue-300' },
  vraag:          { label: 'Vraag',          color: 'bg-purple-100 text-purple-700 border-purple-300' },
  klacht:         { label: 'Klacht',         color: 'bg-orange-100 text-orange-700 border-orange-300' },
  betaling:       { label: 'Betaling',       color: 'bg-green-100 text-green-700 border-green-300' },
  bestelling:     { label: 'Bestelling',     color: 'bg-cyan-100 text-cyan-700 border-cyan-300' },
  marketing:      { label: 'Marketing',      color: 'bg-gray-100 text-gray-600 border-gray-300' },
  spam:           { label: 'Spam',           color: 'bg-gray-100 text-gray-500 border-gray-300' },
  outbound:       { label: 'Verzonden',      color: 'bg-indigo-100 text-indigo-700 border-indigo-300' },
  other:          { label: 'Overig',         color: 'bg-gray-100 text-gray-600 border-gray-300' },
};

const SENTIMENT_ICONS = { positive: '😊', neutral: '😐', negative: '😠' };

const FOLDERS = [
  { key: 'INBOX',   label: 'Inbox',       icon: Tray },
  { key: 'SENT',    label: 'Verzonden',   icon: PaperPlaneTilt },
  { key: 'STARRED', label: 'Met ster',    icon: Star,           filter: { is_starred: true } },
  { key: 'SNOOZED', label: 'Uitgesteld',  icon: Clock },
  { key: 'SPAM',    label: 'Ongewenst',   icon: Prohibit,       isSpam: true },
  { key: 'TRASH',   label: 'Prullenbak',  icon: Trash },
];

// 🚩 Kleur-flags voor handmatige categorisering (6 kleuren)
const FLAG_COLORS = {
  red:    { bg: '#EF4444', label: '🔴 Rood (urgent)' },
  orange: { bg: '#F97316', label: '🟠 Oranje (opvolgen)' },
  yellow: { bg: '#EAB308', label: '🟡 Geel (later)' },
  green:  { bg: '#10B981', label: '🟢 Groen (klaar)' },
  blue:   { bg: '#3B82F6', label: '🔵 Blauw (info)' },
  purple: { bg: '#A855F7', label: '🟣 Paars (project)' },
};

const truncate = (s, n) => (s || '').length > n ? (s || '').slice(0, n).trim() + '…' : (s || '');

export default function MailHubPage() {
  const [accounts, setAccounts] = useState([]);
  const [activeAccount, setActiveAccount] = useState('');
  const [folder, setFolder] = useState('INBOX');
  const [search, setSearch] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState(new Set()); // multi-select voor bulk-acties
  const [compactList, setCompactList] = useState(() => {
    try { return localStorage.getItem('mail_compact') === '1'; } catch { return false; }
  });
  const toggleCompact = () => {
    setCompactList(c => {
      const next = !c;
      try { localStorage.setItem('mail_compact', next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };
  const [activeFlagColor, setActiveFlagColor] = useState(''); // filter op kleur-vlag
  const [flagCounts, setFlagCounts] = useState({}); // {red: N, orange: N, ...}
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loadingMail, setLoadingMail] = useState(false);
  const latestMidRef = useRef(null); // race-guard tegen async fetch van vorige klik
  const [showCompose, setShowCompose] = useState(false);
  const [composePrefill, setComposePrefill] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  // Scheduled-send / Follow-ups / Rules modals + tellers
  const [showScheduled, setShowScheduled] = useState(false);
  const [showFollowUps, setShowFollowUps] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [scheduledCount, setScheduledCount] = useState(0);
  const [followUpCount, setFollowUpCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [premiumBlocked, setPremiumBlocked] = useState(false);
  const [counts, setCounts] = useState({ unread_total: 0, by_category: {} });
  const [activeCategory, setActiveCategory] = useState('');
  const [folders, setFolders] = useState([]);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [showDigest, setShowDigest] = useState(false);
  const [digestData, setDigestData] = useState(null);
  const [digestBusy, setDigestBusy] = useState(false);

  // Auto-open compose-modal als URL ?compose=1&to=...&subject=...&body=...&autoattach_doc_id=...
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('compose') === '1') {
      const toEmail = params.get('to') || '';
      const subj = params.get('subject') || '';
      const bodyText = params.get('body') || '';
      const autoDocId = params.get('autoattach_doc_id') || '';
      const prefill = {};
      if (toEmail) prefill.to = [toEmail];
      if (subj) prefill.subject = subj;
      if (bodyText) prefill.text_body = bodyText;
      if (autoDocId) prefill.autoattach_doc_id = autoDocId;
      setComposePrefill(Object.keys(prefill).length ? prefill : null);
      setShowCompose(true);
      // Verwijder query-params om herladen te voorkomen
      const url = new URL(window.location.href);
      url.search = '';
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  // Resize-state: kolombreedtes (links sidebar = vast 224px, lijst kolom is rekbaar)
  const [sidebarW, setSidebarW] = useState(() => {
    const v = parseInt(localStorage.getItem('mailhub.sidebarW') || '224', 10);
    return Math.max(140, Math.min(360, isNaN(v) ? 224 : v));
  });
  const [listW, setListW] = useState(() => {
    const v = parseInt(localStorage.getItem('mailhub.listW') || '384', 10);
    return Math.max(200, Math.min(900, isNaN(v) ? 384 : v));
  });
  const dragging = useRef(null); // 'sidebar' | 'list' | null
  const startDrag = (which) => (e) => {
    dragging.current = which;
    e.preventDefault();
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };
  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current) return;
      if (dragging.current === 'sidebar') {
        const w = Math.max(140, Math.min(360, e.clientX - 0)); // sidebar begint links op viewport — adjust voor layout-offset
        // Hou rekening met de offset van het Layout-component (~ kan variëren); werk relatief tov pagina-container
        const container = document.querySelector('[data-testid="mail-hub-page"]');
        if (container) {
          const rect = container.getBoundingClientRect();
          const w2 = Math.max(140, Math.min(360, e.clientX - rect.left));
          setSidebarW(w2);
        } else {
          setSidebarW(w);
        }
      } else if (dragging.current === 'list') {
        const container = document.querySelector('[data-testid="mail-hub-page"]');
        if (container) {
          const rect = container.getBoundingClientRect();
          const w = Math.max(220, Math.min(rect.width - sidebarW - 280, e.clientX - rect.left - sidebarW));
          setListW(w);
        }
      }
    };
    const onUp = () => {
      if (dragging.current === 'sidebar') localStorage.setItem('mailhub.sidebarW', String(sidebarW));
      if (dragging.current === 'list') localStorage.setItem('mailhub.listW', String(listW));
      dragging.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [sidebarW, listW]);

  const loadAccounts = useCallback(async () => {
    try {
      const r = await API.get('/mail/accounts');
      setAccounts(r.data || []);
      setPremiumBlocked(false);
    } catch (e) {
      if (e.response?.status === 402) {
        setPremiumBlocked(true);
      }
      setAccounts([]);
    }
  }, []);

  const loadMessages = useCallback(async () => {
    if (premiumBlocked) return;
    setLoading(true);
    try {
      const params = { folder, page, page_size: 50 };
      if (activeAccount) params.account_id = activeAccount;
      if (search) params.search = search;
      if (unreadOnly) params.unread_only = true;
      if (activeCategory) params.category = activeCategory;
      if (activeFlagColor) params.flag_color = activeFlagColor;
      const r = await API.get('/mail/messages', { params });
      setMessages(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [folder, activeAccount, search, unreadOnly, page, premiumBlocked, activeCategory, activeFlagColor]);

  const loadFlagCounts = useCallback(async () => {
    try {
      const r = await API.get('/mail/flag-counts');
      setFlagCounts(r.data?.counts || {});
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadFlagCounts(); }, [loadFlagCounts, folder, messages]);

  const loadCounts = useCallback(async () => {
    try {
      const r = await API.get('/mail/counts');
      setCounts(r.data || { unread_total: 0, by_category: {} });
    } catch {
      setCounts({ unread_total: 0, by_category: {} });
    }
    // Tel pending geplande mails + open follow-ups voor de header-badges
    try {
      const [s, f] = await Promise.all([
        API.get('/mail/scheduled?status=pending').then(r => r.data?.items?.length || 0).catch(() => 0),
        API.get('/mail/follow-ups').then(r => r.data?.count || 0).catch(() => 0),
      ]);
      setScheduledCount(s);
      setFollowUpCount(f);
    } catch { /* ignore */ }
  }, []);

  const loadFolders = useCallback(async () => {
    try {
      const r = await API.get('/mail/folders');
      setFolders(r.data || []);
    } catch {
      setFolders([]);
    }
  }, []);

  const fetchDigest = async () => {
    setDigestBusy(true);
    setShowDigest(true);
    try {
      const r = await API.post('/mail/ai/daily-digest', {});
      setDigestData(r.data || null);
    } catch (e) {
      setDigestData({ summary: 'Briefing mislukt: ' + (e.response?.data?.detail || e.message), top_priorities: [], highlights: [], urgent: [] });
    }
    setDigestBusy(false);
  };

  const moveToFolder = async (mid, folderId) => {
    try {
      await API.post(`/mail/messages/${mid}/move`, { folder: folderId });
      setMessages(ms => ms.filter(x => x.id !== mid));
      if (selected?.id === mid) { setSelected(null); setSelectedId(null); }
      loadCounts();
      loadFolders();
    } catch (e) {
      alert('Verplaatsen mislukt: ' + (e.response?.data?.detail || e.message));
    }
  };

  useEffect(() => { loadAccounts(); }, [loadAccounts]);
  useEffect(() => { loadMessages(); }, [loadMessages]);
  useEffect(() => { loadCounts(); }, [loadCounts, messages.length]);
  useEffect(() => { loadFolders(); }, [loadFolders]);

  const openMessage = async (mid, stub = null) => {
    latestMidRef.current = mid;
    setSelectedId(mid);
    // Toon direct de basis-info uit de lijst (snippet, subject, from) — geen lege state
    if (stub) {
      setSelected({ ...stub, _is_stub: true });
      setLoadingMail(false);
    } else {
      setSelected(null);
      setLoadingMail(true);
    }
    try {
      const r = await API.get(`/mail/messages/${mid}`);
      // Race-guard: alleen update als deze mail nog steeds de meest recent gekozene is
      if (latestMidRef.current !== mid) return;
      setSelected(r.data);
      // Update read-state in list locally
      setMessages(ms => ms.map(m => m.id === mid ? { ...m, is_read: true } : m));
    } catch (e) {
      if (latestMidRef.current === mid) console.error(e);
    } finally {
      if (latestMidRef.current === mid) setLoadingMail(false);
    }
  };

  const doSync = async () => {
    if (!accounts.length) {
      alert('Geen mailbox gekoppeld. Voeg eerst een mailbox toe via "Mailbox toevoegen".');
      return;
    }
    setSyncing(true);
    try {
      for (const acc of accounts.filter(a => a.actief !== false)) {
        await API.post(`/mail/accounts/${acc.id}/sync`);
      }
      await loadMessages();
      await loadAccounts();
    } catch (e) {
      alert('Sync mislukt: ' + (e.response?.data?.detail || e.message));
    }
    setSyncing(false);
  };

  const toggleStar = async (m) => {
    await API.patch(`/mail/messages/${m.id}`, { is_starred: !m.is_starred });
    setMessages(ms => ms.map(x => x.id === m.id ? { ...x, is_starred: !x.is_starred } : x));
    if (selected?.id === m.id) setSelected({ ...selected, is_starred: !selected.is_starred });
  };

  const moveToTrash = async (m) => {
    await API.delete(`/mail/messages/${m.id}`);
    setMessages(ms => ms.filter(x => x.id !== m.id));
    if (selected?.id === m.id) { setSelected(null); setSelectedId(null); }
  };

  const markAsSpam = async (m) => {
    const fromAddr = m.from_email || '';
    if (!fromAddr) { alert('Geen afzender bekend'); return; }
    const blockDomain = window.confirm(
      `Markeer "${fromAddr}" als spam.\n\n` +
      `OK = blokkeer hele domein (*@${(fromAddr.split('@')[1] || '')}) → alle mails van dit bedrijf gaan voortaan automatisch naar Ongewenst.\n` +
      `Annuleer = blokkeer alleen dit ene adres.`
    );
    try {
      await API.post(`/mail/spam/mark/${m.id}`, { block_domain: blockDomain });
      setMessages(ms => ms.filter(x => x.id !== m.id));
      if (selected?.id === m.id) { setSelected(null); setSelectedId(null); }
    } catch (e) {
      alert('Spam-markering mislukt: ' + (e.response?.data?.detail || e.message));
    }
  };

  const unmarkAsSpam = async (m) => {
    try {
      await API.post(`/mail/spam/unmark/${m.id}`);
      setMessages(ms => ms.filter(x => x.id !== m.id));
      if (selected?.id === m.id) { setSelected(null); setSelectedId(null); }
    } catch (e) {
      alert('Ongedaan maken mislukt: ' + (e.response?.data?.detail || e.message));
    }
  };

  // 🚩 Stel kleur-flag in op een mail (en update lokaal)
  const setMailFlagColor = async (m, color) => {
    try {
      await API.post(`/mail/messages/${m.id}/flag-color`, { color });
      setMessages(ms => ms.map(x => x.id === m.id ? { ...x, flag_color: color || undefined } : x));
      if (selected?.id === m.id) setSelected(s => ({ ...s, flag_color: color || undefined }));
      loadFlagCounts();
    } catch (e) {
      alert('Vlag instellen mislukt: ' + (e.response?.data?.detail || e.message));
    }
  };

  // ⏰ Snooze een mail tot een gekozen tijdstip
  const snoozeMail = async (m) => {
    // Snelle keuzes
    const choices = [
      { lbl: '⏰ Vanavond 18:00', getDate: () => { const d = new Date(); d.setHours(18, 0, 0, 0); if (d < new Date()) d.setDate(d.getDate() + 1); return d; } },
      { lbl: '🌅 Morgen 09:00', getDate: () => { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(9, 0, 0, 0); return d; } },
      { lbl: '📅 Volgende week', getDate: () => { const d = new Date(); d.setDate(d.getDate() + 7); d.setHours(9, 0, 0, 0); return d; } },
      { lbl: '📆 Volgende maand', getDate: () => { const d = new Date(); d.setMonth(d.getMonth() + 1); d.setHours(9, 0, 0, 0); return d; } },
      { lbl: '✏ Aangepast...', custom: true },
    ];
    const choice = window.prompt(
      `Stel snooze in voor "${(m.subject || '(geen onderwerp)').slice(0, 40)}":\n\n` +
      choices.map((c, i) => `${i + 1}. ${c.lbl}`).join('\n') +
      `\n\nTyp 1-5 of een datum (YYYY-MM-DD of YYYY-MM-DDTHH:MM):`
    );
    if (!choice) return;
    let untilDate;
    const idx = parseInt(choice, 10);
    if (idx >= 1 && idx <= choices.length && !choices[idx - 1].custom) {
      untilDate = choices[idx - 1].getDate();
    } else if (idx === 5) {
      const custom = window.prompt('Datum/tijd voor unsnooze (YYYY-MM-DD of YYYY-MM-DDTHH:MM):');
      if (!custom) return;
      untilDate = new Date(custom);
    } else {
      untilDate = new Date(choice);
    }
    if (!untilDate || isNaN(untilDate.getTime())) { alert('Ongeldige datum'); return; }
    try {
      await API.post(`/mail/messages/${m.id}/snooze`, { until: untilDate.toISOString() });
      setMessages(ms => ms.filter(x => x.id !== m.id));
      if (selected?.id === m.id) { setSelected(null); setSelectedId(null); }
      alert(`Mail uitgesteld tot ${untilDate.toLocaleString('nl-BE')}`);
    } catch (e) {
      alert('Snooze mislukt: ' + (e.response?.data?.detail || e.message));
    }
  };

  const unsnoozeMail = async (m) => {
    try {
      await API.post(`/mail/messages/${m.id}/unsnooze`);
      setMessages(ms => ms.filter(x => x.id !== m.id));
      if (selected?.id === m.id) { setSelected(null); setSelectedId(null); }
    } catch (e) {
      alert('Unsnooze mislukt: ' + (e.response?.data?.detail || e.message));
    }
  };

  // ── Multi-select bulk-acties ──
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds(prev => {
      if (prev.size === messages.length && messages.length > 0) return new Set();
      return new Set(messages.map(m => m.id));
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const bulkAction = async (action, extra = {}) => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    const confirmMsg = {
      delete: `${ids.length} mail(s) naar prullenbak verplaatsen?`,
      permanent_delete: `${ids.length} mail(s) DEFINITIEF verwijderen? Dit kan niet ongedaan gemaakt worden.`,
      mark_read: '',
      mark_unread: '',
      star: '',
      unstar: '',
      move: '',
    }[action];
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try {
      const r = await API.post('/mail/messages/bulk-action', { ids, action, ...extra });
      // Verwijder de mails uit de lokale lijst als ze verwijderd of verplaatst zijn (folder = anders)
      if (action === 'delete' || action === 'permanent_delete' || action === 'move') {
        setMessages(ms => ms.filter(x => !selectedIds.has(x.id)));
      } else {
        // mark_read/unread/star/unstar → update lokaal
        const upd = action === 'mark_read' ? { is_read: true }
          : action === 'mark_unread' ? { is_read: false }
          : action === 'star' ? { is_starred: true }
          : action === 'unstar' ? { is_starred: false } : {};
        setMessages(ms => ms.map(x => selectedIds.has(x.id) ? { ...x, ...upd } : x));
      }
      clearSelection();
      if (selected && selectedIds.has(selected.id)) { setSelected(null); setSelectedId(null); }
      return r.data;
    } catch (e) {
      alert('Bulk-actie mislukt: ' + (e.response?.data?.detail || e.message));
    }
  };

  if (premiumBlocked) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-8" data-testid="mail-hub-premium-block">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center border-2 border-purple-200">
          <EnvelopeSimple size={48} className="mx-auto text-purple-500 mb-3" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">🔒 Mail Hub is een Pro-feature</h2>
          <p className="text-gray-600 mb-4">
            Koppel je eigen mailbox aan BillFac voor automatische factuur-routing, AI-classificatie en 100% mail-tracking per klant.
          </p>
          <ul className="text-left text-sm text-gray-700 mb-5 space-y-1 inline-block">
            <li>✅ Verstuur vanuit jouw eigen e-mailadres</li>
            <li>✅ Inkomende factuur-PDFs auto-routed</li>
            <li>✅ AI classificeert + suggereert antwoorden</li>
            <li>✅ Volledige timeline per klant</li>
            <li>✅ Open/click-tracking per mail</li>
          </ul>
          <a href="/instellingen" className="btn btn-primary inline-block">Upgrade naar Pro</a>
        </div>
      </div>
    );
  }

  return (
    <div className="mail-content flex flex-col h-[calc(100vh-110px)] bg-white rounded-lg shadow-sm overflow-hidden" data-testid="mail-hub-page">
      {/* TOP TOOLBAR */}
      <div className="flex items-center gap-2 p-3 border-b bg-gray-50">
        <button
          onClick={() => { setComposePrefill(null); setShowCompose(true); }}
          className="px-4 py-2 bg-[#002FA7] hover:bg-[#0040D0] text-white rounded font-semibold inline-flex items-center gap-1.5 transition-colors"
          disabled={!accounts.length}
          title={accounts.length ? "Nieuw bericht opstellen" : "Eerst een mailbox koppelen"}
          data-testid="mail-compose-btn"
        >
          <Plus size={16} weight="bold" /> Nieuw bericht
        </button>
        <button
          onClick={fetchDigest}
          disabled={digestBusy}
          className="px-3 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white text-sm rounded inline-flex items-center gap-1.5 disabled:opacity-50"
          title="AI Secretaris: maak een dagelijkse briefing van inkomende mails"
          data-testid="mail-digest-btn"
        >
          <Sparkle size={14} weight="fill" /> {digestBusy ? '🤖 Bezig...' : 'AI Briefing'}
        </button>
        <button
          onClick={doSync}
          disabled={syncing}
          className="px-3 py-2 bg-white hover:bg-gray-100 border border-gray-300 rounded text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="mail-sync-btn"
        >
          <ArrowsClockwise size={14} weight="bold" className={syncing ? 'animate-spin' : ''} />
          {syncing ? 'Synchroniseren...' : 'Synchroniseer'}
        </button>
        <div className="flex-1 max-w-md relative">
          <MagnifyingGlass size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Zoek op onderwerp, afzender..."
            className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#002FA7]"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            data-testid="mail-search-input"
          />
        </div>
        <label className="flex items-center gap-1.5 text-xs text-gray-600">
          <input type="checkbox" checked={unreadOnly} onChange={(e) => { setUnreadOnly(e.target.checked); setPage(1); }} data-testid="mail-unread-toggle" />
          Alleen ongelezen
        </label>
        <AiTriageButton onDone={loadMessages} />
        <button
          onClick={() => setShowScheduled(true)}
          className="px-3 py-2 bg-white hover:bg-purple-50 border border-purple-300 text-purple-700 rounded text-sm inline-flex items-center gap-1.5"
          title="Geplande mails (klaar voor uitgestelde verzending)"
          data-testid="mail-scheduled-btn"
        >
          🕒 Gepland
          {scheduledCount > 0 && <span className="bg-purple-600 text-white text-[10px] px-1.5 py-0.5 rounded-full">{scheduledCount}</span>}
        </button>
        <button
          onClick={() => setShowFollowUps(true)}
          className="px-3 py-2 bg-white hover:bg-amber-50 border border-amber-300 text-amber-700 rounded text-sm inline-flex items-center gap-1.5"
          title="Follow-up reminders (verzonden mails zonder antwoord)"
          data-testid="mail-followups-btn"
        >
          📌 Follow-ups
          {followUpCount > 0 && <span className="bg-amber-600 text-white text-[10px] px-1.5 py-0.5 rounded-full">{followUpCount}</span>}
        </button>
        <button
          onClick={() => setShowRules(true)}
          className="px-3 py-2 bg-white hover:bg-emerald-50 border border-emerald-300 text-emerald-700 rounded text-sm inline-flex items-center gap-1.5"
          title="Mail-regels (automatisch verwerken inkomende mail)"
          data-testid="mail-rules-btn"
        >
          ⚙️ Regels
        </button>
        <button
          onClick={() => setShowSettings(true)}
          className="px-3 py-2 bg-white hover:bg-gray-100 border border-gray-300 rounded text-sm inline-flex items-center gap-1.5"
          data-testid="mail-settings-btn"
        >
          <Gear size={14} weight="bold" /> Mailboxen
        </button>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* SIDEBAR — folders + accounts */}
        <div className="border-r bg-gray-50 overflow-y-auto p-2 flex-shrink-0" style={{ width: sidebarW }}>
          {/* 🤖 AI Event-voorstellen bovenaan — verschijnt enkel bij ≥1 voorstel */}
          <MailEventProposals />
          {/* 📦 AI Order-voorstellen — verschijnt enkel bij ≥1 voorstel */}
          <MailOrderProposals />
          <div className="mb-3">
            <div className="flex items-center justify-between px-2 mb-1 gap-2">
              <span className="text-[10px] font-bold uppercase text-gray-500 flex-shrink-0">Mappen</span>
              {/* Actieve mailbox-indicator — toont gekoppelde mailbox(en) rechts van "Mappen" */}
              {accounts.length > 0 && (
                <button
                  onClick={() => {
                    if (accounts.length === 1) {
                      setShowSettings(true);
                    } else {
                      // Cycle: '' → account1 → account2 → ... → ''
                      const idx = accounts.findIndex(a => a.id === activeAccount);
                      const next = idx + 1 >= accounts.length ? '' : accounts[idx + 1].id;
                      setActiveAccount(next);
                      setPage(1);
                    }
                  }}
                  className="flex items-center gap-1 text-[10px] truncate hover:bg-gray-200 px-1.5 py-0.5 rounded min-w-0"
                  title={accounts.length === 1
                    ? `Mailbox: ${accounts[0].email} — klik voor instellingen`
                    : (activeAccount
                        ? `Filter: ${accounts.find(a => a.id === activeAccount)?.email || ''} — klik om te wisselen`
                        : `Alle ${accounts.length} mailboxen — klik om te filteren`)}
                  data-testid="mail-active-account-badge"
                >
                  {accounts.length === 1 ? (
                    <>
                      <span className="inline-block w-2 h-2 rounded-full flex-shrink-0" style={{ background: accounts[0].actief !== false ? '#10b981' : '#ef4444' }} />
                      <span className="truncate text-gray-700 font-medium">{accounts[0].email}</span>
                    </>
                  ) : activeAccount ? (
                    <>
                      <span className="inline-block w-2 h-2 rounded-full flex-shrink-0" style={{ background: '#10b981' }} />
                      <span className="truncate text-gray-700 font-medium">{accounts.find(a => a.id === activeAccount)?.email || ''}</span>
                    </>
                  ) : (
                    <span className="text-gray-500 font-semibold">Alle ({accounts.length})</span>
                  )}
                </button>
              )}
            </div>
            {FOLDERS.map(f => {
              const Icon = f.icon;
              const isActive = folder === f.key;
              return (
                <button
                  key={f.key}
                  onClick={() => { setFolder(f.key); setPage(1); setSelected(null); setSelectedId(null); }}
                  className={`w-full text-left px-2 py-1.5 text-sm rounded flex items-center gap-2 ${isActive ? 'bg-[#002FA7] text-white' : 'hover:bg-gray-200 text-gray-700'}`}
                  data-testid={`mail-folder-${f.key}`}
                >
                  <Icon size={14} weight={isActive ? 'bold' : 'regular'} />
                  {f.label}
                </button>
              );
            })}
          </div>
          {/* Custom mappen */}
          <div className="mb-3">
            <div className="flex items-center justify-between px-2 mb-1">
              <span className="text-[10px] font-bold uppercase text-gray-500">Mijn mappen</span>
              <button
                onClick={() => setShowFolderModal(true)}
                className="text-[10px] text-[#8B5CF6] hover:underline font-semibold"
                title="Nieuwe map aanmaken"
                data-testid="mail-folder-add-btn"
              >+ Nieuw</button>
            </div>
            {folders.length === 0 ? (
              <div className="text-[10px] text-gray-400 px-2 italic">Geen eigen mappen — klik op "+ Nieuw"</div>
            ) : folders.map(f => {
              const isActive = folder === f.id;
              return (
                <button
                  key={f.id}
                  onClick={() => { setFolder(f.id); setPage(1); setSelected(null); setSelectedId(null); }}
                  className={`w-full text-left px-2 py-1.5 text-sm rounded flex items-center gap-2 ${isActive ? 'bg-[#8B5CF6] text-white' : 'hover:bg-gray-200 text-gray-700'}`}
                  data-testid={`mail-folder-custom-${f.id}`}
                >
                  <span style={{ fontSize: 14 }}>{f.icon || '📁'}</span>
                  <span className="flex-1 truncate">{f.label}</span>
                  {f.unread > 0 && <span className={`text-[9px] px-1 rounded font-bold ${isActive ? 'bg-white text-[#8B5CF6]' : 'bg-red-500 text-white'}`}>{f.unread}</span>}
                  <span className={`text-[9px] ${isActive ? 'text-white/70' : 'text-gray-400'}`}>{f.count || 0}</span>
                </button>
              );
            })}
          </div>
          {/* 🚩 Kleur-flag filters */}
          {Object.keys(flagCounts).length > 0 && (
            <div className="mb-3" data-testid="mail-flag-filters">
              <div className="text-[10px] font-bold uppercase text-gray-500 px-2 mb-1">🚩 Vlaggen</div>
              <button
                onClick={() => setActiveFlagColor('')}
                className={`w-full text-left px-2 py-1 text-xs rounded ${!activeFlagColor ? 'bg-gray-200 font-semibold' : 'hover:bg-gray-200 text-gray-700'}`}
              >
                Alle kleuren
              </button>
              {Object.entries(FLAG_COLORS).map(([code, c]) => {
                const cnt = flagCounts[code] || 0;
                if (cnt === 0) return null;
                return (
                  <button
                    key={code}
                    onClick={() => setActiveFlagColor(activeFlagColor === code ? '' : code)}
                    className={`w-full text-left px-2 py-1 text-xs rounded flex items-center gap-1.5 ${activeFlagColor === code ? 'bg-gray-200 font-semibold' : 'hover:bg-gray-200 text-gray-700'}`}
                    data-testid={`mail-flag-filter-${code}`}
                  >
                    <span className="inline-block w-3 h-3 rounded-sm border" style={{ background: c.bg }} />
                    <span className="flex-1 truncate">{c.label.split(' ').slice(1).join(' ').replace(/[()]/g, '').trim() || c.label}</span>
                    <span className="text-[10px] text-gray-500">{cnt}</span>
                  </button>
                );
              })}
            </div>
          )}
          {accounts.length > 1 && (
            <div className="mb-3">
              <div className="text-[10px] font-bold uppercase text-gray-500 px-2 mb-1">Mailboxen</div>
              <button
                onClick={() => { setActiveAccount(''); setPage(1); }}
                className={`w-full text-left px-2 py-1.5 text-xs rounded ${!activeAccount ? 'bg-gray-200 font-semibold' : 'hover:bg-gray-200 text-gray-700'}`}
              >Alle ({accounts.length})</button>
              {accounts.map(a => (
                <button
                  key={a.id}
                  onClick={() => { setActiveAccount(a.id); setPage(1); }}
                  className={`w-full text-left px-2 py-1.5 text-xs rounded truncate ${activeAccount === a.id ? 'bg-gray-200 font-semibold' : 'hover:bg-gray-200 text-gray-700'}`}
                  title={a.email}
                  data-testid={`mail-account-${a.id}`}
                >
                  <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ background: a.actief !== false ? '#10b981' : '#ef4444' }} />
                  {a.email}
                </button>
              ))}
            </div>
          )}
          {!accounts.length && (
            <button
              onClick={() => setShowSettings(true)}
              className="w-full px-2 py-2 text-xs bg-[#EEF3FF] hover:bg-[#DDE7FF] text-[#002FA7] rounded border border-[#002FA7]/30 font-semibold mt-2"
              data-testid="mail-add-account-cta"
            >
              + Mailbox koppelen
            </button>
          )}

          {/* 📅 Mini-agenda widget — Outlook-stijl, onderaan de sidebar */}
          <MailSidebarAgenda />
        </div>

        {/* DRAG-HANDLE tussen sidebar en list */}
        <div
          onMouseDown={startDrag('sidebar')}
          className="w-1 cursor-col-resize bg-transparent hover:bg-[#002FA7]/30 transition-colors flex-shrink-0 group relative"
          title="Sleep om sidebar-breedte aan te passen"
          data-testid="mail-resize-sidebar"
        >
          <div className="absolute inset-y-0 -left-0.5 -right-0.5 group-hover:bg-[#002FA7]/20" />
        </div>

        {/* MESSAGE LIST */}
        <div className="border-r overflow-y-auto flex-shrink-0 flex flex-col" style={{ width: listW }}>
          {/* Bulk-toolbar — verschijnt wanneer berichten geselecteerd zijn */}
          {selectedIds.size > 0 && (
            <div className="bg-[#002FA7] text-white px-3 py-2 flex items-center gap-2 flex-shrink-0" data-testid="mail-bulk-toolbar">
              <span className="text-xs font-bold">{selectedIds.size} geselecteerd</span>
              <button
                onClick={() => bulkAction(folder === 'TRASH' ? 'permanent_delete' : 'delete')}
                className="text-xs px-2 py-1 bg-red-500 hover:bg-red-600 rounded font-bold inline-flex items-center gap-1"
                data-testid="mail-bulk-delete"
              >
                <Trash size={12} /> {folder === 'TRASH' ? 'Definitief wissen' : 'Verwijder'}
              </button>
              <button
                onClick={() => bulkAction('mark_read')}
                className="text-xs px-2 py-1 bg-white/15 hover:bg-white/25 rounded inline-flex items-center gap-1"
                title="Markeer als gelezen"
                data-testid="mail-bulk-read"
              >
                <CheckCircle size={12} /> Gelezen
              </button>
              <button
                onClick={() => bulkAction('mark_unread')}
                className="text-xs px-2 py-1 bg-white/15 hover:bg-white/25 rounded"
                title="Markeer als ongelezen"
                data-testid="mail-bulk-unread"
              >
                ● Ongelezen
              </button>
              <button
                onClick={() => bulkAction('star')}
                className="text-xs px-2 py-1 bg-white/15 hover:bg-white/25 rounded inline-flex items-center gap-1"
                title="Met ster markeren"
                data-testid="mail-bulk-star"
              >
                <Star size={12} weight="fill" /> Ster
              </button>
              <select
                onChange={(e) => { if (e.target.value) { bulkAction('move', { folder: e.target.value }); e.target.value = ''; } }}
                className="text-xs px-2 py-1 bg-white/15 hover:bg-white/25 rounded border-0 text-white cursor-pointer"
                title="Verplaats naar map"
                data-testid="mail-bulk-move"
                defaultValue=""
              >
                <option value="" disabled className="text-gray-900">📁 Verplaats...</option>
                <option value="INBOX" className="text-gray-900">📥 Inbox</option>
                <option value="ARCHIVE" className="text-gray-900">📦 Archief</option>
                <option value="SPAM" className="text-gray-900">🚫 Spam</option>
                {folders.map(f => <option key={f.id} value={f.id} className="text-gray-900">{f.icon} {f.label}</option>)}
              </select>
              <button onClick={clearSelection} className="ml-auto text-xs px-2 py-1 hover:bg-white/15 rounded" title="Selectie wissen">✕</button>
            </div>
          )}
          {/* Select-all checkbox — alleen tonen wanneer er berichten zijn */}
          {messages.length > 0 && (
            <div className="px-3 py-1.5 border-b bg-gray-50 flex items-center gap-2 flex-shrink-0">
              <input
                type="checkbox"
                checked={selectedIds.size === messages.length && messages.length > 0}
                ref={el => { if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < messages.length; }}
                onChange={toggleSelectAll}
                className="w-4 h-4 cursor-pointer"
                title={selectedIds.size === messages.length ? 'Deselecteer alles' : 'Selecteer alles'}
                data-testid="mail-select-all"
              />
              <span className="text-[10px] text-gray-500">
                {selectedIds.size === 0 ? `${messages.length} mail${messages.length !== 1 ? 's' : ''}` : `${selectedIds.size}/${messages.length} geselecteerd`}
              </span>
            </div>
          )}
          {/* AI Filter-chips (alleen INBOX) */}
          {folder === 'INBOX' && Object.keys(counts.by_category).length > 0 && (
            <div className="p-2 border-b bg-gradient-to-r from-purple-50 to-pink-50 flex flex-wrap gap-1.5 flex-shrink-0" data-testid="mail-filter-chips">
              <button
                onClick={() => { setActiveCategory(''); setPage(1); }}
                className={`px-2 py-1 text-[10px] rounded border transition-colors ${!activeCategory ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-gray-700 border-gray-300 hover:border-[#002FA7]'}`}
                data-testid="mail-chip-all"
              >
                Alle ({Object.values(counts.by_category).reduce((s, x) => s + (x.total || 0), 0)})
              </button>
              {Object.entries(counts.by_category)
                .sort(([, a], [, b]) => (b.unread || 0) - (a.unread || 0) || (b.total || 0) - (a.total || 0))
                .filter(([cat]) => CATEGORY_BADGES[cat])
                .map(([cat, info]) => {
                  const b = CATEGORY_BADGES[cat];
                  const isActive = activeCategory === cat;
                  return (
                    <button
                      key={cat}
                      onClick={() => { setActiveCategory(cat); setPage(1); }}
                      className={`px-2 py-1 text-[10px] rounded border transition-colors inline-flex items-center gap-1 ${isActive ? 'bg-[#002FA7] text-white border-[#002FA7]' : `${b.color} hover:border-[#002FA7]`}`}
                      data-testid={`mail-chip-${cat}`}
                      title={`${info.total} totaal, ${info.unread} ongelezen`}
                    >
                      {b.label}
                      <span className={`px-1 rounded font-bold ${isActive ? 'bg-white/30' : 'bg-black/10'}`}>{info.total}</span>
                      {info.unread > 0 && (
                        <span className={`px-1 rounded font-bold ${isActive ? 'bg-white text-[#002FA7]' : 'bg-red-500 text-white'}`}>{info.unread}</span>
                      )}
                    </button>
                  );
                })}
            </div>
          )}
          {folder === 'SPAM' && <CommunitySpamBanner />}
          {/* 📃 Compact-modus toggle */}
          <div className="flex items-center justify-end gap-1 px-2 py-1 border-b bg-gray-50">
            <button
              onClick={toggleCompact}
              className={`text-[10px] px-2 py-0.5 rounded border inline-flex items-center gap-1 transition ${compactList ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-100'}`}
              title={compactList ? 'Schakel naar comfortabele weergave (3 regels per mail)' : 'Schakel naar compacte weergave (meer mails zichtbaar)'}
              data-testid="mail-toggle-compact"
            >
              📃 {compactList ? 'Compact' : 'Comfortabel'}
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
          {loading && <div className="p-4 text-center text-sm text-gray-500">Laden...</div>}
          {!loading && messages.length === 0 && (
            <div className="p-8 text-center text-sm text-gray-400">
              <Tray size={32} className="mx-auto mb-2 opacity-50" />
              {!accounts.length ? 'Koppel eerst een mailbox' : `Geen mails in ${FOLDERS.find(f => f.key === folder)?.label || folder}`}
            </div>
          )}
          {messages.map(m => {
            const isActive = m.id === selectedId;
            const isChecked = selectedIds.has(m.id);
            const cat = CATEGORY_BADGES[m.ai_category || m.direction === 'outbound' ? 'outbound' : 'other'];
            // Kleur-vlag heeft prioriteit > urgentie-rand
            const flagBg = FLAG_COLORS[m.flag_color]?.bg;
            const urgencyColor = !flagBg && m.ai_urgency === 'high' ? 'border-l-red-500 bg-red-50/30'
              : !flagBg && m.ai_urgency === 'medium' ? 'border-l-amber-400 bg-amber-50/20'
              : '';
            const flagStyle = flagBg ? { borderLeftColor: flagBg } : {};
            return (
              <div
                key={m.id}
                onClick={() => openMessage(m.id, m)}
                style={flagStyle}
                className={`${compactList ? 'px-2 py-1' : 'px-3 py-2'} border-b cursor-pointer transition-colors border-l-4 ${isActive ? 'bg-[#EEF3FF] border-l-[#002FA7]' : isChecked ? 'bg-blue-50 border-l-[#002FA7]' : `hover:bg-gray-50 ${flagBg ? '' : (urgencyColor || 'border-l-transparent')}`} ${!m.is_read && m.direction === 'inbound' ? 'font-semibold' : ''}`}
                data-testid={`mail-list-item-${m.id}`}
              >
                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggleSelect(m.id)}
                    className={`${compactList ? 'w-3.5 h-3.5 mt-0.5' : 'w-4 h-4 mt-1'} cursor-pointer flex-shrink-0`}
                    data-testid={`mail-select-${m.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    {compactList ? (
                      /* Compact mode: alles op 1-2 regels */
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] text-gray-700 truncate flex items-center gap-1 flex-shrink-0" style={{ width: '32%' }}>
                          {m.ai_urgency === 'high' && <span title="Hoge urgentie">🔴</span>}
                          {m.ai_urgency === 'medium' && <span title="Medium urgentie">🟡</span>}
                          {m.direction === 'outbound' ? `→ ${(m.to_emails || []).join(', ')}` : (m.from_name || m.from_email)}
                        </span>
                        <span className="text-[12px] text-gray-900 truncate flex-1">{m.subject || '(geen onderwerp)'}</span>
                        {(m.attachments || []).length > 0 && <PaperclipHorizontal size={11} className="text-gray-400 flex-shrink-0" />}
                        {m.is_starred && <Star size={11} weight="fill" className="text-amber-500 flex-shrink-0" />}
                        {m.ai_category && (
                          <span className={`text-[9px] px-1 rounded border flex-shrink-0 ${CATEGORY_BADGES[m.ai_category]?.color || ''}`}>
                            {CATEGORY_BADGES[m.ai_category]?.label || m.ai_category}
                          </span>
                        )}
                        <span className="text-[10px] text-gray-400 flex-shrink-0 w-10 text-right">{(m.datum || '').slice(5, 10)}</span>
                      </div>
                    ) : (
                      /* Comfortable mode: 3 regels met preview */
                      <>
                        <div className="flex items-start justify-between gap-2 mb-0.5">
                          <span className="text-xs text-gray-700 truncate flex-1 flex items-center gap-1">
                            {m.ai_urgency === 'high' && <span title="Hoge urgentie">🔴</span>}
                            {m.ai_urgency === 'medium' && <span title="Medium urgentie">🟡</span>}
                            {m.direction === 'outbound' ? `→ ${(m.to_emails || []).join(', ')}` : (m.from_name || m.from_email)}
                          </span>
                          <span className="text-[10px] text-gray-400 flex-shrink-0">{(m.datum || '').slice(5, 16).replace('T', ' ')}</span>
                        </div>
                        <div className="text-sm text-gray-900 truncate mb-0.5">{m.subject || '(geen onderwerp)'}</div>
                        <div className="text-[11px] text-gray-500 truncate">{m.snippet}</div>
                        {m.ai_suggested_action && (
                          <div className="text-[10px] text-purple-600 truncate italic mt-0.5">💡 {m.ai_suggested_action}</div>
                        )}
                        <div className="flex items-center gap-1 mt-1 flex-wrap">
                          {m.ai_category && (
                            <span className={`text-[9px] px-1.5 py-0.5 rounded border ${CATEGORY_BADGES[m.ai_category]?.color || cat?.color || ''}`}>
                              {CATEGORY_BADGES[m.ai_category]?.label || m.ai_category}
                            </span>
                          )}
                          {(m.labels || []).slice(0, 2).map(l => (
                            <span key={l} className="text-[9px] px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded border border-gray-200">{l}</span>
                          ))}
                          {m.ai_sentiment && m.ai_sentiment !== 'neutral' && m.ai_sentiment !== 'none' && (
                            <span className="text-[10px]" title={`Sentiment: ${m.ai_sentiment}`}>{SENTIMENT_ICONS[m.ai_sentiment] || ''}</span>
                          )}
                          {(m.attachments || []).length > 0 && <PaperclipHorizontal size={11} className="text-gray-400" />}
                          {m.is_starred && <Star size={11} weight="fill" className="text-amber-500" />}
                          {folders.length > 0 && (
                            <select
                              value=""
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => { const fid = e.target.value; if (fid) moveToFolder(m.id, fid); }}
                              className="ml-auto text-[9px] border border-gray-200 rounded bg-white text-gray-600 px-1 cursor-pointer hover:border-[#8B5CF6]"
                              title="Verplaats naar map"
                            >
                              <option value="">📁 Verplaats...</option>
                              <option value="INBOX">📥 Inbox</option>
                              {folders.map(f => <option key={f.id} value={f.id}>{f.icon} {f.label}</option>)}
                            </select>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {total > 50 && (
            <div className="p-3 flex items-center justify-between text-xs text-gray-500">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-2 py-1 hover:bg-gray-100 rounded disabled:opacity-30">‹ Vorige</button>
              <span>{(page - 1) * 50 + 1}–{Math.min(page * 50, total)} van {total}</span>
              <button onClick={() => setPage(p => p + 1)} disabled={page * 50 >= total} className="px-2 py-1 hover:bg-gray-100 rounded disabled:opacity-30">Volgende ›</button>
            </div>
          )}
          </div>{/* einde flex-1 overflow-y-auto */}
        </div>{/* einde MESSAGE LIST */}

        {/* DRAG-HANDLE tussen list en reader */}
        <div
          onMouseDown={startDrag('list')}
          className="w-1 cursor-col-resize bg-transparent hover:bg-[#002FA7]/30 transition-colors flex-shrink-0 group relative"
          title="Sleep om kolom-breedte aan te passen"
          data-testid="mail-resize-list"
        >
          <div className="absolute inset-y-0 -left-0.5 -right-0.5 group-hover:bg-[#002FA7]/20" />
        </div>

        {/* READER PANE */}
        <div className="flex-1 overflow-y-auto min-w-0">
          {!selected && !loadingMail && (
            <div className="flex items-center justify-center h-full text-gray-400 text-sm">
              <div className="text-center">
                <EnvelopeSimple size={48} className="mx-auto mb-3 opacity-30" />
                Selecteer een bericht om te lezen
              </div>
            </div>
          )}
          {!selected && loadingMail && (
            <div className="flex items-center justify-center h-full text-gray-500 text-sm" data-testid="mail-loading">
              <div className="text-center">
                <div className="inline-block w-8 h-8 border-2 border-gray-300 border-t-[#002FA7] rounded-full animate-spin mb-3"></div>
                <div>Bericht laden...</div>
              </div>
            </div>
          )}
          {selected && (
            <MailReader
              key={selected.id}
              msg={selected}
              accounts={accounts}
              onToggleStar={() => toggleStar(selected)}
              onTrash={() => moveToTrash(selected)}
              onMarkSpam={() => markAsSpam(selected)}
              onUnmarkSpam={() => unmarkAsSpam(selected)}
              onFlagColor={(color) => setMailFlagColor(selected, color)}
              onSnooze={() => snoozeMail(selected)}
              onUnsnooze={() => unsnoozeMail(selected)}
              onReply={(prefill) => { setComposePrefill(prefill); setShowCompose(true); }}
              onForward={(prefill) => { setComposePrefill(prefill); setShowCompose(true); }}
            />
          )}
        </div>
      </div>

      {/* MODALS */}
      {showCompose && (
        <ComposeModal
          accounts={accounts}
          prefill={composePrefill}
          onClose={() => setShowCompose(false)}
          onSent={() => { setShowCompose(false); loadMessages(); }}
        />
      )}
      {showSettings && (
        <SettingsModal
          accounts={accounts}
          onClose={() => setShowSettings(false)}
          onChange={loadAccounts}
        />
      )}
      {showFolderModal && (
        <FolderModal
          folders={folders}
          onClose={() => setShowFolderModal(false)}
          onChange={() => { loadFolders(); }}
        />
      )}
      {showDigest && (
        <DigestModal
          data={digestData}
          busy={digestBusy}
          onClose={() => { setShowDigest(false); setDigestData(null); }}
          onOpenMail={(mid) => { setShowDigest(false); openMessage(mid); }}
          onRefresh={fetchDigest}
        />
      )}
      {showScheduled && (
        <MailScheduledModal
          onClose={() => setShowScheduled(false)}
          onChange={loadCounts}
        />
      )}
      {showFollowUps && (
        <MailFollowUpsModal
          onClose={() => setShowFollowUps(false)}
          onChange={loadCounts}
        />
      )}
      {showRules && (
        <MailRulesModal
          onClose={() => setShowRules(false)}
        />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// MAIL READER (rechter kolom)
// ════════════════════════════════════════════════════════════════
function MailReader({ msg, onToggleStar, onTrash, onMarkSpam, onUnmarkSpam, onFlagColor, onSnooze, onUnsnooze, onReply, onForward, accounts }) {
  const [aiBusy, setAiBusy] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]); // [{toon, text}, ...]
  const [aiSuggestion, setAiSuggestion] = useState('');
  const [showRaw, setShowRaw] = useState(false);
  const [importingInk, setImportingInk] = useState(false);

  // 📅 AI event-detect — wordt 1x per mail uitgevoerd, resultaat in state
  const [eventDetect, setEventDetect] = useState(null); // {is_event, titel, start, eind, locatie, beschrijving, redenering}
  const [detectingEvent, setDetectingEvent] = useState(false);
  const [creatingEvent, setCreatingEvent] = useState(false);
  useEffect(() => {
    setEventDetect(null);
    if (!msg?.id) return;
    let cancelled = false;
    setDetectingEvent(true);
    API.post(`/mail/messages/${msg.id}/detect-event`)
      .then(r => { if (!cancelled) setEventDetect(r.data); })
      .catch(() => { if (!cancelled) setEventDetect(null); })
      .finally(() => { if (!cancelled) setDetectingEvent(false); });
    return () => { cancelled = true; };
  }, [msg?.id]);

  // Maak event aan vanuit mail (gebruik AI-suggestie als beschikbaar)
  const createEventFromMail = async (useAi = false) => {
    setCreatingEvent(true);
    try {
      const now = new Date();
      now.setMinutes(0, 0, 0);
      now.setHours(now.getHours() + 1);
      const defaultStart = now.toISOString();
      const defaultEind = new Date(now.getTime() + 60 * 60 * 1000).toISOString();
      const payload = {
        titel: useAi && eventDetect?.titel ? eventDetect.titel : (msg.subject || 'Afspraak vanuit mail'),
        start: useAi && eventDetect?.start ? eventDetect.start : defaultStart,
        eind: useAi && eventDetect?.eind ? eventDetect.eind : defaultEind,
        all_day: useAi ? Boolean(eventDetect?.all_day) : false,
        locatie: useAi && eventDetect?.locatie ? eventDetect.locatie : '',
        beschrijving: useAi && eventDetect?.beschrijving
          ? `${eventDetect.beschrijving}\n\n— Vanuit mail: "${msg.subject}" van ${msg.from_email}`
          : `Mail "${msg.subject || ''}" van ${msg.from_email || ''}`,
        kleur: '#8B5CF6',
        bron_mail_id: msg.id,
        bron_mail_subject: msg.subject,
      };
      await API.post('/agenda/events', payload);
      alert(`📅 Event "${payload.titel}" aangemaakt — bekijk in Agenda`);
    } catch (e) {
      alert('Event aanmaken mislukt: ' + (e.response?.data?.detail || e.message));
    }
    setCreatingEvent(false);
  };

  // Vraag 3 reply-suggesties in parallel: vriendelijk, professioneel, kordaat
  const doSuggestReply3 = async () => {
    setAiBusy(true);
    setAiSuggestions([]);
    const tonen = [
      { code: 'vriendelijk', label: '😊 Vriendelijk' },
      { code: 'professioneel', label: '💼 Professioneel' },
      { code: 'kordaat', label: '⚡ Kordaat' },
    ];
    try {
      const results = await Promise.all(
        tonen.map(t =>
          API.post(`/mail/ai/suggest-reply/${msg.id}`, { taal: 'Nederlands', toon: t.code })
            .then(r => ({ toon: t.code, label: t.label, text: r.data.suggestion || '' }))
            .catch(() => ({ toon: t.code, label: t.label, text: '' }))
        )
      );
      setAiSuggestions(results.filter(r => r.text));
    } catch (e) {
      alert('AI suggesties mislukt: ' + (e.response?.data?.detail || e.message));
    }
    setAiBusy(false);
  };

  const doSuggestReply = doSuggestReply3; // backward-compat alias

  const applySuggestion = (suggestionText) => {
    onReply({
      to: [msg.from_email],
      subject: msg.subject?.startsWith('Re:') ? msg.subject : `Re: ${msg.subject || ''}`,
      html_body: (suggestionText || '').replace(/\n/g, '<br>'),
      text_body: suggestionText,
      in_reply_to_id: msg.id,
      klant_id: msg.klant_id,
    });
  };

  const useAiSuggestion = () => applySuggestion(aiSuggestion);

  const startReply = () => {
    onReply({
      to: [msg.from_email],
      subject: msg.subject?.startsWith('Re:') ? msg.subject : `Re: ${msg.subject || ''}`,
      html_body: `<br><br><blockquote style="border-left:3px solid #ccc;padding-left:12px;color:#666"><p><strong>Op ${(msg.datum || '').slice(0, 16).replace('T', ' ')} schreef ${msg.from_name || msg.from_email}:</strong></p>${msg.html_body || ''}</blockquote>`,
      text_body: '',
      in_reply_to_id: msg.id,
      klant_id: msg.klant_id,
    });
  };

  const startForward = () => {
    onForward({
      to: [],
      subject: msg.subject?.startsWith('Fwd:') ? msg.subject : `Fwd: ${msg.subject || ''}`,
      html_body: `<br><br>--- Doorgestuurd bericht ---<br><strong>Van:</strong> ${msg.from_name || ''} &lt;${msg.from_email}&gt;<br><strong>Datum:</strong> ${(msg.datum || '').slice(0, 16).replace('T', ' ')}<br><strong>Onderwerp:</strong> ${msg.subject}<br><br>${msg.html_body || ''}`,
      attachments: [],
    });
  };

  const downloadAtt = async (att) => {
    try {
      const r = await API.get(`/mail/messages/${msg.id}/attachment/${att.id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: att.mime }));
      const a = document.createElement('a');
      a.href = url; a.download = att.filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      alert('Download mislukt: ' + (e.response?.data?.detail || e.message));
    }
  };

  const downloadAllAtts = async () => {
    try {
      const r = await API.get(`/mail/messages/${msg.id}/attachments-zip`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/zip' }));
      // Probeer filename uit Content-Disposition header te halen
      const cd = r.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^";]+)"?/);
      const zipName = match ? match[1] : `bijlages_${msg.id}.zip`;
      const a = document.createElement('a');
      a.href = url; a.download = zipName;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      alert('ZIP-download mislukt: ' + (e.response?.data?.detail || e.message));
    }
  };

  // 📥 Bulk import alle PDF/UBL bijlages → inkomende facturen + AI-parse
  const importAttsToInkomende = async () => {
    const candidates = (msg.attachments || []).filter(a => /\.(pdf|xml|ubl)$/i.test(a.filename || '') && !a.inkomende_document_id);
    if (candidates.length === 0) {
      alert('Geen niet-geïmporteerde PDF/UBL bijlages gevonden');
      return;
    }
    if (!window.confirm(
      `${candidates.length} bijlage(s) importeren naar Inkomende facturen?\n\n` +
      candidates.map(a => `• ${a.filename}`).join('\n') +
      `\n\nDaarna start AI automatisch met parsen (leverancier, bedrag, vervaldag).`
    )) return;
    setImportingInk(true);
    try {
      const r = await API.post(`/mail/messages/${msg.id}/import-attachments-to-inkomende`, { ai_parse: true });
      const newIds = r.data?.new_ids || [];
      // Voor elke nieuwe inkomende factuur → trigger AI-parse via bestaande endpoint
      let aiOk = 0, aiFail = 0;
      for (const did of newIds) {
        try {
          await API.post(`/inkomende-documenten/${did}/ai-parse`);
          aiOk++;
        } catch (e) {
          aiFail++;
          console.warn('AI-parse fout voor', did, e);
        }
      }
      alert(
        `✓ ${r.data.imported} bijlage(s) geïmporteerd\n` +
        `${aiOk > 0 ? `🤖 ${aiOk} succesvol AI-geparsed` : ''}` +
        `${aiFail > 0 ? `\n⚠ ${aiFail} AI-parse(s) mislukt — handmatig invullen in Inkomende facturen` : ''}` +
        `${r.data.skipped_existing > 0 ? `\n(${r.data.skipped_existing} reeds geïmporteerd, overgeslagen)` : ''}` +
        `\n\nGa naar "Inkomende facturen" om ze te bekijken en goed te keuren.`
      );
    } catch (e) {
      alert('Import mislukt: ' + (e.response?.data?.detail || e.message));
    }
    setImportingInk(false);
  };

  return (
    <div className="p-5" data-testid="mail-reader">
      <div className="flex items-start justify-between mb-3 pb-3 border-b">
        <div className="flex-1 min-w-0">
          <h2 className="text-xl font-bold text-gray-900 mb-2">{msg.subject || '(geen onderwerp)'}</h2>
          {/* Outlook-stijl header tabel — Van / Aan / Ontvangen */}
          <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
            <div className="text-gray-500 font-semibold">Van:</div>
            <div className="text-gray-800">
              <span className="font-semibold">{msg.from_name || msg.from_email}</span>
              {msg.from_name && msg.from_email && <span className="text-gray-400 ml-1">&lt;{msg.from_email}&gt;</span>}
            </div>
            <div className="text-gray-500 font-semibold">Aan:</div>
            <div className="text-gray-700 truncate">{(msg.to_emails || []).join(', ') || '—'}</div>
            {msg.cc_emails?.length > 0 && (
              <>
                <div className="text-gray-500 font-semibold">Cc:</div>
                <div className="text-gray-700 truncate">{msg.cc_emails.join(', ')}</div>
              </>
            )}
            <div className="text-gray-500 font-semibold">Ontvangen:</div>
            <div className="text-gray-700">
              {(msg.datum || '').slice(0, 16).replace('T', ' ')}
              {msg.ai_category && (
                <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded border ${CATEGORY_BADGES[msg.ai_category]?.color || ''}`}>
                  {CATEGORY_BADGES[msg.ai_category]?.label || msg.ai_category}
                </span>
              )}
            </div>
          </div>
          {msg.ai_summary && (
            <div className="mt-2 px-3 py-2 bg-purple-50 border border-purple-200 rounded text-xs text-purple-800">
              <Sparkle size={12} weight="fill" className="inline mr-1" /> AI samenvatting: {msg.ai_summary}
            </div>
          )}
          {/* 🏢 Klant-context paneel — UNIEK voor BillFac */}
          <CustomerContextPanel msg={msg} />
          {/* ⚡ Smart quick-replies — 3 1-zin antwoorden */}
          <QuickReplies msg={msg} onPick={applySuggestion} />
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onToggleStar} title="Met ster markeren" className="p-2 hover:bg-gray-100 rounded" data-testid="mail-star-btn">
            <Star size={16} weight={msg.is_starred ? 'fill' : 'regular'} className={msg.is_starred ? 'text-amber-500' : 'text-gray-400'} />
          </button>
          {/* 🚩 Kleur-flag picker */}
          <div className="relative group">
            <button title="Kleur-vlag" className="p-2 hover:bg-gray-100 rounded inline-flex items-center" data-testid="mail-flag-btn">
              {msg.flag_color ? (
                <span className="inline-block w-4 h-4 rounded-sm border" style={{ background: FLAG_COLORS[msg.flag_color]?.bg || '#999' }} />
              ) : (
                <Flag size={16} className="text-gray-400" />
              )}
            </button>
            <div className="absolute right-0 top-full mt-1 hidden group-hover:flex bg-white border border-gray-200 shadow-xl rounded p-1 gap-1 z-10">
              {Object.entries(FLAG_COLORS).map(([code, c]) => (
                <button
                  key={code}
                  onClick={() => onFlagColor(code)}
                  className="w-6 h-6 rounded border hover:scale-110 transition-transform"
                  style={{ background: c.bg, borderColor: msg.flag_color === code ? '#000' : c.bg }}
                  title={c.label}
                  data-testid={`mail-flag-${code}`}
                />
              ))}
              {msg.flag_color && (
                <button
                  onClick={() => onFlagColor('')}
                  className="px-1.5 text-xs text-gray-500 hover:text-red-600"
                  title="Vlag wissen"
                  data-testid="mail-flag-clear"
                >✕</button>
              )}
            </div>
          </div>
          {/* ⏰ Snooze knop */}
          <button onClick={onSnooze} title="Snooze (uitstellen)" className="p-2 hover:bg-blue-50 rounded text-gray-500 hover:text-blue-600" data-testid="mail-snooze-btn">
            <Clock size={16} />
          </button>
          {msg.folder !== 'SPAM' ? (
            <button onClick={onMarkSpam} title="Markeer als spam (afzender blokkeren)" className="p-2 hover:bg-orange-50 rounded text-orange-600" data-testid="mail-spam-btn">
              <Prohibit size={16} />
            </button>
          ) : (
            <button onClick={onUnmarkSpam} title="Geen spam — terug naar Inbox" className="p-2 hover:bg-green-50 rounded text-green-600" data-testid="mail-unspam-btn">
              <ShieldCheck size={16} weight="bold" />
            </button>
          )}
          <button onClick={onTrash} title="Naar prullenbak" className="p-2 hover:bg-red-50 rounded text-red-500" data-testid="mail-trash-btn">
            <Trash size={16} />
          </button>
        </div>
      </div>

      {/* Actie-knoppen */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <button onClick={startReply} className="px-3 py-1.5 bg-[#002FA7] hover:bg-[#0040D0] text-white text-sm rounded inline-flex items-center gap-1.5" data-testid="mail-reply-btn">
          <ArrowBendUpLeft size={14} weight="bold" /> Antwoord
        </button>
        <button onClick={startForward} className="px-3 py-1.5 bg-white hover:bg-gray-100 border border-gray-300 text-sm rounded inline-flex items-center gap-1.5" data-testid="mail-forward-btn">
          <ArrowBendDoubleUpRight size={14} weight="bold" /> Doorsturen
        </button>
        {msg.direction === 'inbound' && (
          <button onClick={doSuggestReply} disabled={aiBusy} className="px-3 py-1.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white text-sm rounded inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="mail-ai-reply-btn">
            <Sparkle size={14} weight="fill" /> {aiBusy ? 'Bezig...' : '🤖 3 AI Antwoorden'}
          </button>
        )}
        <button onClick={() => setShowRaw(s => !s)} className="px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700">
          {showRaw ? 'HTML preview' : 'Toon raw HTML'}
        </button>
        <button
          onClick={() => createEventFromMail(false)}
          disabled={creatingEvent}
          className="ml-auto px-3 py-1.5 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white text-sm rounded inline-flex items-center gap-1.5 disabled:opacity-50"
          title="Maak een agenda-event aan vanuit deze mail"
          data-testid="mail-create-event-btn"
        >
          📅 {creatingEvent ? 'Bezig...' : 'Maak afspraak'}
        </button>
      </div>

      {/* 🤖 AI Event-detectie banner — toont alleen als AI een afspraak herkende */}
      {detectingEvent && (
        <div className="mb-3 p-2 bg-gray-50 border border-gray-200 rounded text-xs text-gray-500 italic">
          🤖 AI controleert of deze mail een afspraak bevat...
        </div>
      )}
      {!detectingEvent && eventDetect?.is_event && eventDetect.confidence >= 0.5 && (
        <div className="mb-4 p-3 bg-gradient-to-r from-violet-50 to-indigo-50 border-2 border-violet-300 rounded-lg" data-testid="mail-ai-event-banner">
          <div className="flex items-start gap-3">
            <div className="text-2xl">📅</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-violet-800 mb-1">
                🤖 AI herkende een afspraakverzoek
                <span className="ml-2 text-[10px] font-normal text-violet-600">
                  (zekerheid {Math.round((eventDetect.confidence || 0) * 100)}%)
                </span>
              </div>
              <div className="text-xs text-gray-700 space-y-0.5 mb-2">
                {eventDetect.titel && <div><span className="font-semibold">Titel:</span> {eventDetect.titel}</div>}
                {eventDetect.start && (
                  <div><span className="font-semibold">Wanneer:</span> {eventDetect.start.slice(0, 16).replace('T', ' ')}
                    {eventDetect.eind && ` → ${eventDetect.eind.slice(11, 16)}`}
                  </div>
                )}
                {eventDetect.locatie && <div><span className="font-semibold">Locatie:</span> {eventDetect.locatie}</div>}
                {eventDetect.redenering && <div className="italic text-gray-500">💡 {eventDetect.redenering}</div>}
              </div>
              <button
                onClick={() => createEventFromMail(true)}
                disabled={creatingEvent}
                className="px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold rounded inline-flex items-center gap-1 disabled:opacity-50"
                data-testid="mail-ai-event-accept"
              >
                ➕ {creatingEvent ? 'Bezig...' : 'Toevoegen aan Agenda'}
              </button>
              <button
                onClick={() => setEventDetect(null)}
                className="ml-2 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded"
              >
                Negeren
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI 3-suggesties box — toont vriendelijk/professioneel/kordaat naast elkaar */}
      {aiSuggestions.length > 0 && (
        <div className="mb-4 p-3 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded" data-testid="mail-ai-suggestions">
          <div className="flex items-start justify-between mb-2">
            <div className="text-xs font-bold text-purple-700 inline-flex items-center gap-1">
              <Sparkle size={12} weight="fill" /> {aiSuggestions.length} AI-antwoord-suggesties — klik om te gebruiken
            </div>
            <button onClick={() => setAiSuggestions([])} className="text-purple-400 hover:text-purple-700"><X size={14} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {aiSuggestions.map(s => (
              <div key={s.toon} className="bg-white border border-purple-200 rounded p-2.5 flex flex-col" data-testid={`mail-ai-suggestion-${s.toon}`}>
                <div className="text-[11px] font-bold uppercase tracking-wider text-purple-700 mb-1.5">{s.label}</div>
                <div className="text-xs text-gray-800 whitespace-pre-wrap mb-2 flex-1 line-clamp-6 max-h-40 overflow-y-auto">{s.text}</div>
                <button
                  onClick={() => applySuggestion(s.text)}
                  className="px-2 py-1 bg-purple-600 hover:bg-purple-700 text-white text-xs rounded font-semibold w-full"
                  data-testid={`mail-ai-use-${s.toon}`}
                >Gebruik deze →</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* (Legacy single-suggestion - alleen tonen als oude flow zou worden gebruikt) */}
      {aiSuggestion && (
        <div className="mb-4 p-3 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded">
          <div className="flex items-start justify-between mb-2">
            <div className="text-xs font-bold text-purple-700 inline-flex items-center gap-1">
              <Sparkle size={12} weight="fill" /> AI-suggestie voor antwoord
            </div>
            <button onClick={() => setAiSuggestion('')} className="text-purple-400 hover:text-purple-700"><X size={14} /></button>
          </div>
          <div className="text-sm text-gray-800 whitespace-pre-wrap mb-2">{aiSuggestion}</div>
          <button onClick={useAiSuggestion} className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white text-xs rounded font-semibold">Gebruik deze tekst →</button>
        </div>
      )}

      {/* Attachments */}
      {(msg.attachments || []).length > 0 && (
        <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div className="text-[10px] font-bold uppercase text-gray-500">📎 {msg.attachments.length} bijlage(n)</div>
            <div className="flex gap-1.5">
              {/* Tel hoeveel pdf/ubl/xml bijlages er zijn die nog NIET geïmporteerd zijn */}
              {msg.attachments.some(a => /\.(pdf|xml|ubl)$/i.test(a.filename || '') && !a.inkomende_document_id) && (
                <button
                  onClick={importAttsToInkomende}
                  disabled={importingInk}
                  className="text-[10px] px-2.5 py-1 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white rounded font-bold inline-flex items-center gap-1 disabled:opacity-50"
                  title="Importeer alle PDF/UBL bijlages als inkomende facturen + AI-parse"
                  data-testid="mail-import-inkomende"
                >
                  📥 {importingInk ? 'Bezig...' : 'Importeer als inkomende facturen'}
                </button>
              )}
              {msg.attachments.length > 1 && (
                <button
                  onClick={downloadAllAtts}
                  className="text-[10px] px-2.5 py-1 bg-[#002FA7] hover:bg-blue-800 text-white rounded font-bold inline-flex items-center gap-1"
                  title="Download alle bijlages als ZIP-bestand"
                  data-testid="mail-download-all-attachments"
                >
                  <DownloadSimple size={11} weight="bold" /> Alle (.zip)
                </button>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {msg.attachments.map(att => (
              <button
                key={att.id}
                onClick={() => downloadAtt(att)}
                className="px-3 py-2 bg-white border border-gray-300 hover:border-[#002FA7] rounded text-xs text-left inline-flex items-center gap-2 max-w-xs"
                data-testid={`mail-attachment-${att.id}`}
              >
                <FileText size={14} className="text-gray-500 flex-shrink-0" />
                <div className="flex-1 truncate">
                  <div className="font-semibold text-gray-800 truncate">{att.filename}</div>
                  <div className="text-gray-500 text-[10px]">{(att.size / 1024).toFixed(1)} KB
                    {att.inkomende_document_id && <span className="text-green-600 ml-1">✓ in Inkomende facturen</span>}
                  </div>
                </div>
                <DownloadSimple size={14} className="text-gray-400 flex-shrink-0" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Body */}
      <div className="prose prose-sm max-w-none">
        {showRaw ? (
          <pre className="text-xs bg-gray-50 p-3 rounded overflow-auto">{msg.html_body || msg.text_body}</pre>
        ) : msg.html_body ? (
          <iframe
            title="mail-body"
            srcDoc={msg.html_body}
            sandbox=""
            className="w-full min-h-[400px] border-0"
          />
        ) : (
          <pre className="whitespace-pre-wrap text-sm text-gray-800">{msg.text_body || msg.snippet}</pre>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// COMPOSE MODAL (nieuwe / antwoord / doorsturen)
// ════════════════════════════════════════════════════════════════
function ComposeModal({ accounts, prefill, onClose, onSent }) {
  const [accountId, setAccountId] = useState(() => (accounts.find(a => a.is_default) || accounts[0])?.id || '');
  const [to, setTo] = useState((prefill?.to || []).join(', '));
  const [cc, setCc] = useState((prefill?.cc || []).join(', '));
  const [bcc, setBcc] = useState('');
  const [subject, setSubject] = useState(prefill?.subject || '');
  const [body, setBody] = useState(prefill?.text_body || prefill?.html_body?.replace(/<[^>]+>/g, '') || '');
  const [wysiwygHtml, setWysiwygHtml] = useState(prefill?.html_body || ''); // WYSIWYG-modus
  const [useWysiwyg, setUseWysiwyg] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [showCcBcc, setShowCcBcc] = useState(!!cc);
  const [polishing, setPolishing] = useState(false);
  const [polishToon, setPolishToon] = useState('professioneel');
  const [polishTaal, setPolishTaal] = useState('Nederlands');
  const [polishHistorie, setPolishHistorie] = useState([]);  // stack van vorige versies → undo
  const [includeSignature, setIncludeSignature] = useState(true);
  // Scheduled-send + Follow-up reminders
  const [scheduleAt, setScheduleAt] = useState('');  // datetime-local string, lege string = direct verzenden
  const [followUpDays, setFollowUpDays] = useState(0);  // 0 = geen reminder
  // Mail-templates
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [templateFilter, setTemplateFilter] = useState('');
  const fileRef = useRef(null);
  const wysiwygRef = useRef(null);

  // Laad templates bij open
  useEffect(() => {
    API.get('/mail/templates').then(r => setTemplates(r.data?.templates || [])).catch(() => {});
  }, []);

  // ─── Snippets (tekst-expansie via /commando) ──────────────────────────
  // ─── Auto-attach via URL deep-link (bv. /mail?compose=1&autoattach_doc_id=LB-XXX) ─
  // Wordt gebruikt vanuit andere modules (leverbon-verzendmelding) om het
  // bijbehorende document automatisch als PDF toe te voegen aan de bijlages.
  useEffect(() => {
    const docId = prefill?.autoattach_doc_id;
    if (!docId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await API.get(`/documenten/${docId}/pdf-base64`);
        if (cancelled || !r.data?.content_b64) return;
        const newAtt = {
          filename: r.data.filename,
          mime: r.data.mime,
          content_b64: r.data.content_b64,
          size: r.data.size || Math.round(r.data.content_b64.length * 0.75),
        };
        setAttachments(prev => {
          if (prev.some(p => p.filename === newAtt.filename)) return prev;
          return [...prev, newAtt];
        });
      } catch (e) { /* stil falen: gebruiker ziet bijlage gewoon niet */ }
    })();
    return () => { cancelled = true; };
  }, [prefill?.autoattach_doc_id]);

  const [snippets, setSnippets] = useState([]);
  useEffect(() => {
    API.get('/mail/snippets').then(r => setSnippets(r.data?.items || [])).catch(() => {});
  }, []);

  // ─── Auto-attach openstaande facturen (uniek voor BillFac) ──────────────
  // Bij invullen van 'to' adres: zoek klant en toon openstaande facturen
  const [openFacturen, setOpenFacturen] = useState({ klant: null, facturen: [], totaal_open: 0 });
  const [showFacturenPicker, setShowFacturenPicker] = useState(false);
  const [pickerSelected, setPickerSelected] = useState({});
  const [pickerLoading, setPickerLoading] = useState(false);

  // Debounce: zoek klant op TO-email
  useEffect(() => {
    const first = (to || '').split(/[,;]/)[0].trim();
    if (!first || !first.includes('@')) {
      setOpenFacturen({ klant: null, facturen: [], totaal_open: 0 });
      return;
    }
    const t = setTimeout(async () => {
      try {
        const r = await API.get(`/mail/open-facturen-by-email?email=${encodeURIComponent(first)}`);
        if (r.data?.matched) {
          setOpenFacturen({ klant: r.data.klant, facturen: r.data.facturen || [], totaal_open: r.data.totaal_open || 0 });
        } else {
          setOpenFacturen({ klant: null, facturen: [], totaal_open: 0 });
        }
      } catch (e) { /* stil falen */ }
    }, 600);
    return () => clearTimeout(t);
  }, [to]);

  const openFacturenPicker = () => {
    const init = {};
    openFacturen.facturen.forEach(f => { init[f.id] = true; });  // standaard alles aangevinkt
    setPickerSelected(init);
    setShowFacturenPicker(true);
  };

  const doAttachFacturen = async () => {
    const ids = Object.entries(pickerSelected).filter(([, v]) => v).map(([k]) => k);
    if (!ids.length) { setShowFacturenPicker(false); return; }
    setPickerLoading(true);
    try {
      const r = await API.post('/mail/auto-attach-facturen', {
        klant_id: openFacturen.klant.id,
        factuur_ids: ids,
      });
      const newAtt = (r.data?.attachments || []).map(a => ({
        filename: a.filename,
        mime: a.mime,
        content_b64: a.content_b64,
        size: Math.round(a.content_b64.length * 0.75),  // benaderen vanuit base64
      }));
      setAttachments(prev => {
        // ontdupliceer op filename
        const have = new Set(prev.map(p => p.filename));
        return [...prev, ...newAtt.filter(n => !have.has(n.filename))];
      });
      // ── Substitueer placeholders in subject + body met de gekozen factuur ──
      // We gebruiken de oudste gekozen factuur (= meest dringend) als bron-data.
      const gekozen = openFacturen.facturen.filter(f => ids.includes(f.id));
      const eersteFac = gekozen[0];
      if (eersteFac) {
        const vars = {
          klant_naam: openFacturen.klant?.naam || '',
          naam: openFacturen.klant?.naam || '',
          factuurnummer: eersteFac.nummer || eersteFac.id || '',
          factuur_nummer: eersteFac.nummer || eersteFac.id || '',
          bedrag: '€ ' + (Number(eersteFac.totaal_incl) || 0).toLocaleString('nl-BE', { minimumFractionDigits: 2 }),
          totaal: '€ ' + (Number(eersteFac.totaal_incl) || 0).toLocaleString('nl-BE', { minimumFractionDigits: 2 }),
          vervaldag: eersteFac.vervaldatum ? new Date(eersteFac.vervaldatum).toLocaleDateString('nl-BE') : '',
          vervaldatum: eersteFac.vervaldatum ? new Date(eersteFac.vervaldatum).toLocaleDateString('nl-BE') : '',
          datum: eersteFac.datum ? new Date(eersteFac.datum).toLocaleDateString('nl-BE') : '',
          ogm: eersteFac.ogm || '',
          mededeling: eersteFac.ogm || '',
        };
        const replace = (txt) => {
          if (!txt) return txt;
          return txt.replace(/\{([a-z_]+)\}/gi, (m, key) => {
            const k = key.toLowerCase();
            return vars[k] !== undefined && vars[k] !== '' ? vars[k] : m;
          });
        };
        setSubject(s => replace(s));
        setBody(b => replace(b));
        // WYSIWYG: alleen state updaten — niet rechtstreeks innerHTML schrijven
        // (dat veroorzaakt React removeChild-fout bij volgende renders).
        // setWysiwygHtml triggert NIET automatisch een re-render van de contentEditable;
        // gebruiker moet voor nu in textarea-modus werken voor auto-substitutie.
        if (useWysiwyg) {
          setWysiwygHtml(prev => replace(prev || body.replace(/\n/g, '<br>')));
        }
        toast.success(`Factuur gekoppeld + variabelen ingevuld (${eersteFac.nummer})`);
      }
      setShowFacturenPicker(false);
    } catch (e) {
      alert('Facturen koppelen mislukt: ' + (e.response?.data?.detail || e.message));
    }
    setPickerLoading(false);
  };

  // Detect "/trigger" in textarea: bij spatie of enter → expandeer
  const expandSnippetInTextarea = (textareaEl) => {
    if (!textareaEl || snippets.length === 0) return false;
    const val = textareaEl.value;
    const cursor = textareaEl.selectionStart;
    const before = val.slice(0, cursor);
    const m = /(?:^|\s)(\/[a-z0-9_]+)\s$/i.exec(before);
    if (!m) return false;
    const trig = m[1].slice(1).toLowerCase();
    const snip = snippets.find(s => s.trigger === trig);
    if (!snip) return false;
    const startIdx = cursor - m[0].length + (m[0].startsWith(' ') ? 1 : 0);
    const newVal = val.slice(0, startIdx) + snip.body + ' ' + val.slice(cursor);
    setBody(newVal);
    setTimeout(() => {
      try {
        const np = startIdx + snip.body.length + 1;
        textareaEl.selectionStart = textareaEl.selectionEnd = np;
        textareaEl.focus();
      } catch (e) { /* ignore */ }
    }, 0);
    return true;
  };

  // Wrap onChange voor body-textarea → detecteer snippet
  const handleBodyChange = (e) => {
    setBody(e.target.value);
    // Slechts triggeren na spatie-toets
    if (e.nativeEvent?.data === ' ') {
      setTimeout(() => expandSnippetInTextarea(e.target), 0);
    }
  };

  // Apply een template — vraagt context-data (klant_id, doc_id) van prefill indien beschikbaar
  const applyTemplate = async (tpl) => {
    try {
      const r = await API.post(`/mail/templates/${tpl.id}/render`, {
        klant_id: prefill?.klant_id || '',
        doc_id: prefill?.doc_id || '',
      });
      if (r.data.subject) setSubject(r.data.subject);
      if (r.data.html_body) {
        setWysiwygHtml(r.data.html_body);
        // ook plaintext-versie genereren voor de textarea-modus
        setBody(r.data.html_body.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim());
      }
      if (r.data.text_body) setBody(r.data.text_body);
      setShowTemplates(false);
    } catch (e) {
      alert('Template laden mislukt: ' + (e.response?.data?.detail || e.message));
    }
  };

  const seedTemplates = async () => {
    try {
      const r = await API.post('/mail/templates/seed-defaults');
      if (r.data.inserted) {
        const t = await API.get('/mail/templates');
        setTemplates(t.data?.templates || []);
        alert(`${r.data.inserted} voorbeeld-templates aangemaakt`);
      } else {
        alert('Templates bestaan al');
      }
    } catch (e) {
      alert('Seed mislukt: ' + (e.response?.data?.detail || e.message));
    }
  };

  // WYSIWYG commands via document.execCommand (browser-built-in)
  const wysiwygCmd = (cmd, value = null) => {
    document.execCommand(cmd, false, value);
    wysiwygRef.current?.focus();
    setWysiwygHtml(wysiwygRef.current?.innerHTML || '');
  };

  const doPolish = async () => {
    if (!body.trim()) {
      alert('Type eerst iets in het bericht-veld.');
      return;
    }
    setPolishing(true);
    try {
      const r = await API.post('/mail/ai/polish', {
        text: body,
        taal: polishTaal,
        toon: polishToon,
        behoud_lengte: false,
        context_subject: subject,
        context_to: to,
      });
      const polished = (r.data?.polished || '').trim();
      if (polished && polished !== body) {
        setPolishHistorie(h => [...h, body]);  // push huidige naar undo-stack
        setBody(polished);
      } else {
        alert('De tekst was al goed — geen verbetering nodig.');
      }
    } catch (e) {
      alert('AI verbeter mislukt: ' + (e.response?.data?.detail || e.message));
    }
    setPolishing(false);
  };

  const undoPolish = () => {
    if (polishHistorie.length === 0) return;
    const last = polishHistorie[polishHistorie.length - 1];
    setBody(last);
    setPolishHistorie(h => h.slice(0, -1));
  };

  const addFiles = async (files) => {
    const arr = Array.from(files || []);
    const out = [];
    for (const f of arr) {
      if (f.size > 15 * 1024 * 1024) {
        alert(`"${f.name}" is te groot (max 15MB)`);
        continue;
      }
      const b64 = await new Promise((res) => {
        const reader = new FileReader();
        reader.onloadend = () => res(reader.result.split(',')[1]);
        reader.readAsDataURL(f);
      });
      out.push({ filename: f.name, mime: f.type || 'application/octet-stream', content_b64: b64, size: f.size });
    }
    setAttachments(a => [...a, ...out]);
  };

  const doSend = async () => {
    const toList = to.split(/[,;]/).map(s => s.trim()).filter(s => s.includes('@'));
    const ccList = cc.split(/[,;]/).map(s => s.trim()).filter(s => s.includes('@'));
    const bccList = bcc.split(/[,;]/).map(s => s.trim()).filter(s => s.includes('@'));
    if (!toList.length) { alert('Minstens 1 ontvanger nodig'); return; }
    if (!subject.trim()) { if (!confirm('Geen onderwerp ingevuld. Toch verzenden?')) return; }
    setSending(true);
    try {
      // In WYSIWYG-modus: gebruik de live HTML uit de editor. Anders: textarea body → HTML.
      const liveHtml = useWysiwyg
        ? (wysiwygRef.current?.innerHTML || wysiwygHtml)
        : ((prefill?.html_body && body === (prefill?.text_body || prefill?.html_body?.replace(/<[^>]+>/g, '')))
            ? prefill.html_body  // user heeft body niet aangepast → behoud HTML
            : body.replace(/\n/g, '<br>'));
      // Plain-text fallback voor mail-clients zonder HTML support
      const plainText = useWysiwyg
        ? liveHtml.replace(/<br\s*\/?>/g, '\n').replace(/<\/p>/g, '\n\n').replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim()
        : body;
      const basePayload = {
        account_id: accountId,
        to: toList,
        cc: ccList,
        bcc: bccList,
        subject,
        html_body: liveHtml,
        text_body: plainText,
        in_reply_to_id: prefill?.in_reply_to_id,
        klant_id: prefill?.klant_id,
        document_id: prefill?.document_id,
        attachments,
        track_open: true,
        track_clicks: true,
        include_signature: includeSignature,
        follow_up_days: followUpDays > 0 ? followUpDays : null,
      };
      // Scheduled-send: plan voor later
      if (scheduleAt) {
        const dt = new Date(scheduleAt);
        if (Number.isNaN(dt.getTime())) { alert('Ongeldige datum/tijd'); setSending(false); return; }
        if (dt.getTime() < Date.now() + 30 * 1000) { alert('Datum moet minimaal 30s in de toekomst zijn'); setSending(false); return; }
        await API.post('/mail/scheduled', { ...basePayload, scheduled_at: dt.toISOString() });
        toast.success(`Mail gepland voor ${dt.toLocaleString('nl-BE')}`);
      } else {
        await API.post('/mail/send', basePayload);
        toast.success('Mail verzonden');
      }
      onSent();
    } catch (e) {
      alert('Verzenden mislukt: ' + (e.response?.data?.detail || e.message));
    }
    setSending(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="mail-compose-modal">
      <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b flex items-center justify-between bg-gray-50">
          <h3 className="text-lg font-bold text-gray-900 inline-flex items-center gap-2"><EnvelopeSimple size={20} weight="fill" /> Nieuw bericht</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-200 rounded"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3 overflow-y-auto flex-1">
          {accounts.length > 1 && (
            <div>
              <label className="text-[10px] font-bold uppercase text-gray-500">Verstuur vanuit</label>
              <select value={accountId} onChange={(e) => setAccountId(e.target.value)} className="field-input mt-1" data-testid="mail-compose-account">
                {accounts.map(a => <option key={a.id} value={a.id}>{a.naam} &lt;{a.email}&gt;</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="text-[10px] font-bold uppercase text-gray-500">Aan</label>
            <MailContactAutocomplete
              value={to}
              onChange={setTo}
              placeholder="Typ naam of e-mailadres..."
              testId="mail-compose-to-autocomplete"
            />
          </div>
          {showCcBcc ? (
            <>
              <div>
                <label className="text-[10px] font-bold uppercase text-gray-500">Cc</label>
                <MailContactAutocomplete
                  value={cc}
                  onChange={setCc}
                  placeholder="Cc-ontvangers..."
                  testId="mail-compose-cc-autocomplete"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase text-gray-500">Bcc</label>
                <MailContactAutocomplete
                  value={bcc}
                  onChange={setBcc}
                  placeholder="Bcc (blinde kopie)..."
                  testId="mail-compose-bcc-autocomplete"
                />
              </div>
            </>
          ) : (
            <button onClick={() => setShowCcBcc(true)} className="text-xs text-[#002FA7] hover:underline">+ Cc / Bcc toevoegen</button>
          )}
          <div>
            <label className="text-[10px] font-bold uppercase text-gray-500">Onderwerp</label>
            <input type="text" className="field-input mt-1" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="mail-compose-subject" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1 flex-wrap gap-1">
              <label className="text-[10px] font-bold uppercase text-gray-500">Bericht</label>
              <div className="flex items-center gap-1.5 flex-wrap">
                {/* 📑 Mail-templates dropdown */}
                <TemplateDropdown
                  templates={templates}
                  templateFilter={templateFilter}
                  setTemplateFilter={setTemplateFilter}
                  show={showTemplates}
                  setShow={setShowTemplates}
                  applyTemplate={applyTemplate}
                  seedTemplates={seedTemplates}
                />
                {/* WYSIWYG-mode toggle */}
                <button
                  type="button"
                  onClick={() => {
                    if (!useWysiwyg && !wysiwygHtml) {
                      setWysiwygHtml(body.replace(/\n/g, '<br>'));
                    } else if (useWysiwyg) {
                      setBody((wysiwygRef.current?.innerHTML || wysiwygHtml).replace(/<br\s*\/?>/g, '\n').replace(/<[^>]+>/g, ''));
                    }
                    setUseWysiwyg(w => !w);
                  }}
                  className={`text-[10px] px-2 py-1 border rounded ${useWysiwyg ? 'bg-blue-100 border-blue-300 text-blue-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                  title="Schakel WYSIWYG-editor (vet/cursief/lijsten)"
                  data-testid="mail-compose-wysiwyg-toggle"
                >
                  {useWysiwyg ? '📝 WYSIWYG aan' : '📝 WYSIWYG'}
                </button>
                <select
                  value={polishToon}
                  onChange={(e) => setPolishToon(e.target.value)}
                  className="text-[10px] px-1.5 py-1 border border-gray-300 rounded bg-white"
                  title="Toon voor AI verbeter"
                  data-testid="mail-compose-polish-toon"
                >
                  <option value="casual">💬 Casual</option>
                  <option value="vriendelijk">😊 Vriendelijk</option>
                  <option value="professioneel">💼 Professioneel</option>
                  <option value="formeel">🎩 Formeel</option>
                  <option value="kordaat">⚡ Kordaat</option>
                  <option value="bestelling">📋 Bestelling</option>
                  <option value="aanmaning">🚨 Aanmaning</option>
                </select>
                <select
                  value={polishTaal}
                  onChange={(e) => setPolishTaal(e.target.value)}
                  className="text-[10px] px-1.5 py-1 border border-gray-300 rounded bg-white"
                  title="Taal voor AI verbeter"
                  data-testid="mail-compose-polish-taal"
                >
                  <option value="Nederlands">🇳🇱 NL</option>
                  <option value="Frans">🇫🇷 FR</option>
                  <option value="Engels">🇬🇧 EN</option>
                  <option value="Duits">🇩🇪 DE</option>
                </select>
                {polishHistorie.length > 0 && (
                  <button
                    type="button"
                    onClick={undoPolish}
                    className="text-[10px] px-2 py-1 bg-white hover:bg-gray-100 border border-gray-300 rounded inline-flex items-center gap-1"
                    title={`${polishHistorie.length} undo-stap${polishHistorie.length === 1 ? '' : 'pen'} beschikbaar`}
                    data-testid="mail-compose-polish-undo"
                  >
                    ↶ Ongedaan
                  </button>
                )}
                <button
                  type="button"
                  onClick={doPolish}
                  disabled={polishing || !body.trim()}
                  className="text-[10px] px-2 py-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold rounded inline-flex items-center gap-1 disabled:opacity-50 transition-all"
                  title="Laat AI je tekst professioneler maken"
                  data-testid="mail-compose-polish-btn"
                >
                  <Sparkle size={11} weight="fill" /> {polishing ? 'Bezig...' : 'AI verbeter'}
                </button>
              </div>
            </div>
            {/* WYSIWYG toolbar — alleen tonen in WYSIWYG-modus */}
            {useWysiwyg && (
              <div className="flex items-center gap-1 mb-1 bg-gray-50 border border-gray-200 rounded px-2 py-1 flex-wrap" data-testid="mail-compose-wysiwyg-toolbar">
                <button type="button" onClick={() => wysiwygCmd('bold')} className="px-2 py-0.5 text-sm font-bold hover:bg-gray-200 rounded" title="Vet (Ctrl+B)"><strong>B</strong></button>
                <button type="button" onClick={() => wysiwygCmd('italic')} className="px-2 py-0.5 text-sm italic hover:bg-gray-200 rounded" title="Cursief (Ctrl+I)"><em>I</em></button>
                <button type="button" onClick={() => wysiwygCmd('underline')} className="px-2 py-0.5 text-sm underline hover:bg-gray-200 rounded" title="Onderlijnd (Ctrl+U)">U</button>
                <span className="w-px h-4 bg-gray-300 mx-1"></span>
                <button type="button" onClick={() => wysiwygCmd('insertUnorderedList')} className="px-2 py-0.5 text-sm hover:bg-gray-200 rounded" title="Opsomming">• Lijst</button>
                <button type="button" onClick={() => wysiwygCmd('insertOrderedList')} className="px-2 py-0.5 text-sm hover:bg-gray-200 rounded" title="Genummerde lijst">1. Lijst</button>
                <span className="w-px h-4 bg-gray-300 mx-1"></span>
                <button type="button" onClick={() => { const url = prompt('Link URL:'); if (url) wysiwygCmd('createLink', url); }} className="px-2 py-0.5 text-sm hover:bg-gray-200 rounded text-blue-700" title="Voeg link toe">🔗 Link</button>
                <button type="button" onClick={() => wysiwygCmd('unlink')} className="px-2 py-0.5 text-sm hover:bg-gray-200 rounded text-gray-500" title="Link verwijderen">✕ Link</button>
                <span className="w-px h-4 bg-gray-300 mx-1"></span>
                <button type="button" onClick={() => wysiwygCmd('removeFormat')} className="px-2 py-0.5 text-sm hover:bg-gray-200 rounded text-red-600" title="Verwijder opmaak">⌫ Opmaak</button>
                <span className="ml-auto text-[10px] text-gray-500 italic">Tip: Ctrl+B/I/U sneltoetsen werken ook</span>
              </div>
            )}
            {/* Body editor: WYSIWYG contenteditable OF gewone textarea */}
            {useWysiwyg ? (
              <div
                ref={wysiwygRef}
                contentEditable
                suppressContentEditableWarning
                className="field-input text-sm min-h-[280px] max-h-[480px] overflow-y-auto"
                style={{ outline: 'none' }}
                dangerouslySetInnerHTML={{ __html: wysiwygHtml || body.replace(/\n/g, '<br>') }}
                onBlur={(e) => setWysiwygHtml(e.currentTarget.innerHTML)}
                data-testid="mail-compose-body-wysiwyg"
              />
            ) : (
              <textarea
                className="field-input font-mono text-sm"
                rows={12}
                value={body}
                onChange={handleBodyChange}
                placeholder="Beste,...  (tip: typ /dank gevolgd door spatie voor snippet-expansie)"
                data-testid="mail-compose-body"
              />
            )}
            <div className="text-[10px] text-gray-400 mt-1 flex justify-between">
              <span>{useWysiwyg ? 'WYSIWYG-modus actief — opmaak wordt mee verzonden' : <>Tip: typ <code className="bg-gray-100 px-1 rounded">/dank</code> + spatie → auto-uitbreiding ({snippets.length} snippets actief)</>}</span>
              <span>{(useWysiwyg ? (wysiwygRef.current?.innerText || '') : body).length} tekens</span>
            </div>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase text-gray-500">Bijlages</label>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <button type="button" onClick={() => fileRef.current?.click()} className="px-3 py-1.5 bg-white hover:bg-gray-100 border border-gray-300 text-sm rounded inline-flex items-center gap-1.5" data-testid="mail-compose-attach">
                <PaperclipHorizontal size={14} /> Bestand kiezen
              </button>
              <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => addFiles(e.target.files)} />
              {/* 📎 Voeg openstaande facturen toe (auto-detected via to-adres) */}
              {openFacturen.facturen.length > 0 && (
                <button
                  type="button"
                  onClick={openFacturenPicker}
                  className="px-3 py-1.5 bg-gradient-to-r from-amber-50 to-orange-50 hover:from-amber-100 hover:to-orange-100 border border-amber-400 text-amber-900 text-sm rounded inline-flex items-center gap-1.5 font-semibold"
                  title={`${openFacturen.klant?.naam} heeft ${openFacturen.facturen.length} openstaande factu(u)r(en) — totaal € ${openFacturen.totaal_open.toLocaleString('nl-BE', { minimumFractionDigits: 2 })}`}
                  data-testid="mail-compose-attach-open-facturen"
                >
                  📎 {openFacturen.facturen.length} openstaande factu{openFacturen.facturen.length === 1 ? 'ur' : 'ren'} koppelen
                  <span className="text-[10px] bg-amber-200 px-1.5 py-0.5 rounded">€ {openFacturen.totaal_open.toLocaleString('nl-BE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                </button>
              )}
              <span className="text-xs text-gray-500">{attachments.length} bijlage(n)</span>
            </div>
            {attachments.length > 0 && (
              <div className="mt-2 space-y-1">
                {attachments.map((a, i) => (
                  <div key={i} className="px-2 py-1 bg-gray-50 border border-gray-200 rounded flex items-center justify-between text-xs">
                    <span className="truncate">📎 {a.filename} <span className="text-gray-400">({(a.size / 1024).toFixed(1)} KB)</span></span>
                    <button onClick={() => setAttachments(att => att.filter((_, j) => j !== i))} className="text-red-500 hover:text-red-700"><X size={12} /></button>
                  </div>
                ))}
              </div>
            )}
            {/* ─── Picker-modal voor openstaande facturen ─── */}
            {showFacturenPicker && (
              <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4" onClick={() => setShowFacturenPicker(false)} data-testid="mail-compose-facturen-picker">
                <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                  <div className="px-4 py-3 border-b bg-amber-50 flex items-center justify-between">
                    <h4 className="font-bold text-amber-900 inline-flex items-center gap-2">
                      <PaperclipHorizontal size={18} /> Openstaande facturen voor {openFacturen.klant?.naam}
                    </h4>
                    <button onClick={() => setShowFacturenPicker(false)} className="p-1 hover:bg-amber-100 rounded"><X size={16} /></button>
                  </div>
                  <div className="p-4 overflow-y-auto flex-1">
                    <p className="text-xs text-gray-600 mb-3">Kies welke openstaande facturen je als PDF wil meesturen.</p>
                    <div className="space-y-1.5">
                      {openFacturen.facturen.map(f => (
                        <label key={f.id} className="flex items-center gap-2 p-2 border border-gray-200 hover:bg-gray-50 rounded cursor-pointer" data-testid={`mail-compose-factuur-toggle-${f.id}`}>
                          <input
                            type="checkbox"
                            checked={!!pickerSelected[f.id]}
                            onChange={(e) => setPickerSelected(s => ({ ...s, [f.id]: e.target.checked }))}
                            className="w-4 h-4"
                          />
                          <div className="flex-1">
                            <div className="font-mono text-sm font-bold text-gray-900">{f.nummer}</div>
                            <div className="text-[11px] text-gray-500">Datum: {f.datum} · Vervalt: {f.vervaldatum}</div>
                          </div>
                          <div className="text-sm font-bold text-amber-700">€ {f.totaal_incl.toLocaleString('nl-BE', { minimumFractionDigits: 2 })}</div>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="px-4 py-3 bg-gray-50 border-t flex items-center justify-end gap-2">
                    <button onClick={() => setShowFacturenPicker(false)} className="px-3 py-1.5 text-sm border border-gray-300 bg-white hover:bg-gray-50 rounded">Annuleer</button>
                    <button
                      onClick={doAttachFacturen}
                      disabled={pickerLoading || Object.values(pickerSelected).every(v => !v)}
                      className="px-4 py-1.5 text-sm bg-amber-500 hover:bg-amber-600 disabled:bg-gray-300 text-white font-bold rounded inline-flex items-center gap-1.5"
                      data-testid="mail-compose-facturen-attach-btn"
                    >
                      <PaperclipHorizontal size={14} />
                      {pickerLoading ? 'Bezig...' : `Koppel ${Object.values(pickerSelected).filter(Boolean).length} factu${Object.values(pickerSelected).filter(Boolean).length === 1 ? 'ur' : 'ren'}`}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="px-5 py-3 bg-gray-50 border-t flex items-center justify-between flex-shrink-0 flex-wrap gap-2">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="text-xs text-gray-500 inline-flex items-center gap-1">
              <CheckCircle size={12} className="text-green-500" /> Open + click tracking
            </div>
            <label className="text-xs text-gray-600 inline-flex items-center gap-1 cursor-pointer" title="Mailbox-handtekening automatisch toevoegen aan deze mail">
              <input
                type="checkbox"
                checked={includeSignature}
                onChange={(e) => setIncludeSignature(e.target.checked)}
                data-testid="mail-compose-include-signature"
              />
              📝 Handtekening
            </label>
            {/* Scheduled-send: plan voor later */}
            <label className="text-xs text-gray-600 inline-flex items-center gap-1 cursor-pointer" title="Plan deze mail om op een later moment automatisch te versturen">
              🕒 Plan:
              <input
                type="datetime-local"
                value={scheduleAt}
                onChange={(e) => setScheduleAt(e.target.value)}
                min={new Date(Date.now() + 60 * 1000).toISOString().slice(0, 16)}
                className="px-1.5 py-0.5 border border-gray-300 rounded text-xs"
                data-testid="mail-compose-schedule-at"
              />
              {scheduleAt && (
                <button
                  type="button"
                  onClick={() => setScheduleAt('')}
                  className="text-red-500 hover:text-red-700 px-1"
                  title="Annuleer plannen"
                >
                  ✕
                </button>
              )}
            </label>
            {/* Follow-up reminder: na X dagen herinneren als geen antwoord */}
            <label className="text-xs text-gray-600 inline-flex items-center gap-1 cursor-pointer" title="Herinner mij als ik na X dagen nog geen antwoord heb ontvangen">
              📌 Follow-up na
              <input
                type="number"
                min="0"
                max="365"
                step="1"
                value={followUpDays || ''}
                onChange={(e) => setFollowUpDays(Math.max(0, Math.min(365, parseInt(e.target.value, 10) || 0)))}
                placeholder="0"
                className="w-12 px-1.5 py-0.5 border border-gray-300 rounded text-xs text-center"
                data-testid="mail-compose-follow-up-days"
              />
              <span className="text-gray-500">dagen</span>
            </label>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} disabled={sending} className="btn btn-secondary btn-sm">Annuleer</button>
            <button
              onClick={doSend}
              disabled={sending}
              className={`btn btn-sm inline-flex items-center gap-1.5 ${scheduleAt ? 'bg-purple-600 hover:bg-purple-700 text-white' : 'btn-primary'}`}
              data-testid="mail-compose-send"
            >
              {sending
                ? '⏳ Bezig...'
                : scheduleAt
                  ? <>🕒 Plan verzending</>
                  : <><PaperPlaneTilt size={14} weight="bold" /> Verzend</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// SETTINGS MODAL (mailboxen beheren)
// ════════════════════════════════════════════════════════════════
function SettingsModal({ accounts, onClose, onChange }) {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b flex items-center justify-between bg-gray-50">
          <h3 className="text-lg font-bold text-gray-900">📬 Mailboxen beheren</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-200 rounded"><X size={18} /></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1">
          {!showForm && (
            <>
              {accounts.length === 0 ? (
                <div className="text-center py-8 text-sm text-gray-500">
                  <EnvelopeSimple size={36} className="mx-auto mb-2 opacity-40" />
                  Nog geen mailbox gekoppeld.
                </div>
              ) : (
                <div className="space-y-2 mb-4">
                  {accounts.map(a => (
                    <AccountRow key={a.id} acc={a} onChange={onChange} onEdit={() => { setEditingId(a.id); setShowForm(true); }} />
                  ))}
                </div>
              )}
              <button
                onClick={() => { setEditingId(null); setShowForm(true); }}
                className="w-full px-4 py-3 bg-[#002FA7] hover:bg-[#0040D0] text-white rounded font-semibold inline-flex items-center justify-center gap-2"
                data-testid="mail-add-mailbox-btn"
              >
                <Plus size={16} weight="bold" /> Nieuwe mailbox toevoegen (IMAP/SMTP)
              </button>
              <M365ConnectButton onChange={onChange} />
            </>
          )}
          {showForm && (
            <AccountForm
              accountId={editingId}
              accounts={accounts}
              onCancel={() => setShowForm(false)}
              onSaved={() => { setShowForm(false); onChange(); }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function AccountRow({ acc, onChange, onEdit }) {
  const [busy, setBusy] = useState(false);
  const doDelete = async () => {
    if (!confirm(`Mailbox "${acc.email}" verwijderen? (Mails blijven bewaard.)`)) return;
    setBusy(true);
    try {
      await API.delete(`/mail/accounts/${acc.id}`);
      onChange();
    } catch (e) {
      alert('Verwijderen mislukt: ' + (e.response?.data?.detail || e.message));
    }
    setBusy(false);
  };
  const doTest = async () => {
    setBusy(true);
    try {
      const r = await API.post(`/mail/accounts/${acc.id}/test`);
      if (r.data.ok) alert('✓ Verbinding OK!');
      else alert('✗ Verbinding mislukt: ' + r.data.error);
    } catch (e) {
      alert('Test mislukt: ' + (e.response?.data?.detail || e.message));
    }
    setBusy(false);
  };
  return (
    <div className="p-3 border border-gray-200 rounded bg-gray-50 flex items-center justify-between" data-testid={`mailbox-row-${acc.id}`}>
      <div className="flex-1">
        <div className="font-semibold text-sm text-gray-900 flex items-center gap-2">
          {acc.naam} <span className="text-xs font-normal text-gray-500">&lt;{acc.email}&gt;</span>
          {acc.is_default && <span className="text-[10px] px-1.5 py-0.5 bg-[#002FA7] text-white rounded">Standaard</span>}
          {acc.actief === false && <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded">Inactief</span>}
        </div>
        <div className="text-xs text-gray-500 mt-0.5">
          IMAP: {acc.imap_host}:{acc.imap_port} • SMTP: {acc.smtp_host}:{acc.smtp_port}
        </div>
        <div className="text-xs text-gray-400 mt-0.5">
          📥 {acc.totaal_ontvangen || 0} ontvangen • 📤 {acc.totaal_verzonden || 0} verzonden
          {acc.laatste_sync && <span> • Laatste sync: {acc.laatste_sync.slice(0, 16).replace('T', ' ')}</span>}
        </div>
        {acc.auto_route_facturen && <div className="text-[10px] text-green-600 mt-0.5">✓ Auto-route facturen naar Inkomende facturen</div>}
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <button onClick={doTest} disabled={busy} className="px-2 py-1 text-xs bg-white hover:bg-blue-50 border border-blue-200 text-blue-700 rounded disabled:opacity-50">Test</button>
        <button onClick={onEdit} disabled={busy} className="px-2 py-1 text-xs bg-white hover:bg-gray-100 border border-gray-300 rounded disabled:opacity-50">Bewerken</button>
        <button onClick={doDelete} disabled={busy} className="px-2 py-1 text-xs bg-white hover:bg-red-50 border border-red-200 text-red-600 rounded disabled:opacity-50">Verwijder</button>
      </div>
    </div>
  );
}

function AccountForm({ accountId, accounts, onCancel, onSaved }) {
  const editing = accountId ? accounts.find(a => a.id === accountId) : null;
  const [form, setForm] = useState(() => editing ? {
    naam: editing.naam || '',
    email: editing.email || '',
    imap_host: editing.imap_host || '',
    imap_port: editing.imap_port || 993,
    imap_ssl: editing.imap_ssl !== false,
    smtp_host: editing.smtp_host || '',
    smtp_port: editing.smtp_port || 587,
    smtp_tls: editing.smtp_tls !== false,
    username: editing.username || '',
    password: '',
    is_default: editing.is_default || false,
    auto_route_facturen: editing.auto_route_facturen !== false,
    poll_interval_min: editing.poll_interval_min || 5,
    signature_html: editing.signature_html || '',
    signature_text: editing.signature_text || '',
    signature_active: editing.signature_active !== false,
  } : {
    naam: '', email: '', imap_host: '', imap_port: 993, imap_ssl: true,
    smtp_host: '', smtp_port: 587, smtp_tls: true, username: '', password: '',
    is_default: false, auto_route_facturen: true, poll_interval_min: 5,
    signature_html: '', signature_text: '', signature_active: true,
  });
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Auto-fill presets bij e-mail-domein
  useEffect(() => {
    if (!form.email.includes('@') || editing) return;
    const domain = form.email.split('@')[1].toLowerCase();
    API.get(`/mail/provider-preset?email_addr=${encodeURIComponent(form.email)}`)
      .then(r => {
        if (r.data.preset) {
          setForm(f => ({
            ...f,
            imap_host: f.imap_host || r.data.preset.imap_host,
            imap_port: r.data.preset.imap_port,
            imap_ssl: r.data.preset.imap_ssl,
            smtp_host: f.smtp_host || r.data.preset.smtp_host,
            smtp_port: r.data.preset.smtp_port,
            smtp_tls: r.data.preset.smtp_tls,
            username: f.username || f.email,
            naam: f.naam || f.email.split('@')[0],
          }));
        }
      })
      .catch(() => {});
  }, [form.email, editing]);

  const doSave = async () => {
    setSaving(true);
    setTestResult(null);
    try {
      if (editing) {
        await API.put(`/mail/accounts/${editing.id}`, { ...form, password: form.password || undefined });
      } else {
        await API.post('/mail/accounts', form);
      }
      onSaved();
    } catch (e) {
      setTestResult({ ok: false, error: e.response?.data?.detail || e.message });
    }
    setSaving(false);
  };

  return (
    <div className="space-y-3" data-testid="mailbox-form">
      <h4 className="text-sm font-bold text-gray-700">{editing ? 'Mailbox bewerken' : 'Nieuwe mailbox toevoegen'}</h4>
      <div className="text-xs text-blue-700 bg-blue-50 p-2 rounded border border-blue-200">
        💡 Tip: voor Gmail en Office365 moet je vaak een <strong>App-wachtwoord</strong> aanmaken (i.p.v. je gewone wachtwoord) — zoek &quot;Gmail app password&quot; of &quot;Microsoft app password&quot; in Google.
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] font-bold uppercase text-gray-500">Naam (weergave)</label>
          <input className="field-input mt-1" value={form.naam} onChange={(e) => setForm(f => ({ ...f, naam: e.target.value }))} placeholder="Verkoop" />
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase text-gray-500">E-mailadres</label>
          <input className="field-input mt-1" type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} placeholder="verkoop@bedrijf.be" data-testid="mailbox-form-email" />
        </div>
      </div>
      <div>
        <label className="text-[10px] font-bold uppercase text-gray-500">Gebruikersnaam (vaak = e-mailadres)</label>
        <input className="field-input mt-1" value={form.username} onChange={(e) => setForm(f => ({ ...f, username: e.target.value }))} />
      </div>
      <div>
        <label className="text-[10px] font-bold uppercase text-gray-500">{editing ? 'Nieuw wachtwoord (laat leeg om te behouden)' : 'Wachtwoord / App-wachtwoord'}</label>
        <input className="field-input mt-1" type="password" value={form.password} onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))} autoComplete="new-password" data-testid="mailbox-form-password" />
      </div>
      <div className="grid grid-cols-3 gap-3 p-3 bg-gray-50 rounded border border-gray-200">
        <div className="col-span-3 text-[10px] font-bold uppercase text-gray-500">📥 IMAP (inkomend)</div>
        <div className="col-span-2">
          <label className="text-[10px] text-gray-500">Server</label>
          <input className="field-input mt-1" value={form.imap_host} onChange={(e) => setForm(f => ({ ...f, imap_host: e.target.value }))} placeholder="imap.combell.com" />
        </div>
        <div>
          <label className="text-[10px] text-gray-500">Poort</label>
          <input className="field-input mt-1" type="number" value={form.imap_port} onChange={(e) => setForm(f => ({ ...f, imap_port: parseInt(e.target.value) || 993 }))} />
        </div>
        <label className="col-span-3 text-xs flex items-center gap-2"><input type="checkbox" checked={form.imap_ssl} onChange={(e) => setForm(f => ({ ...f, imap_ssl: e.target.checked }))} /> SSL/TLS gebruiken</label>
      </div>
      <div className="grid grid-cols-3 gap-3 p-3 bg-gray-50 rounded border border-gray-200">
        <div className="col-span-3 text-[10px] font-bold uppercase text-gray-500">📤 SMTP (uitgaand)</div>
        <div className="col-span-2">
          <label className="text-[10px] text-gray-500">Server</label>
          <input className="field-input mt-1" value={form.smtp_host} onChange={(e) => setForm(f => ({ ...f, smtp_host: e.target.value }))} placeholder="smtp.combell.com" />
        </div>
        <div>
          <label className="text-[10px] text-gray-500">Poort</label>
          <input className="field-input mt-1" type="number" value={form.smtp_port} onChange={(e) => setForm(f => ({ ...f, smtp_port: parseInt(e.target.value) || 587 }))} />
        </div>
        <label className="col-span-3 text-xs flex items-center gap-2"><input type="checkbox" checked={form.smtp_tls} onChange={(e) => setForm(f => ({ ...f, smtp_tls: e.target.checked }))} /> STARTTLS gebruiken</label>
      </div>
      <div className="space-y-2 p-3 bg-purple-50 rounded border border-purple-200">
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-bold uppercase text-purple-700">📝 E-mail handtekening</span>
          <label className="text-xs flex items-center gap-1 cursor-pointer">
            <input type="checkbox" checked={form.signature_active} onChange={(e) => setForm(f => ({ ...f, signature_active: e.target.checked }))} data-testid="mailbox-form-signature-active" />
            <span>Auto toevoegen bij verzenden</span>
          </label>
        </div>
        <textarea
          className="field-input text-xs font-mono"
          rows={5}
          value={form.signature_text}
          onChange={(e) => setForm(f => ({ ...f, signature_text: e.target.value, signature_html: e.target.value.replace(/\n/g, '<br>') }))}
          placeholder={`Met vriendelijke groet,\n\nJan Janssens\nAlphaprofiles BV\nwww.alphaprofiles.be — info@alphaprofiles.be\nTel: +32 9 ...`}
          data-testid="mailbox-form-signature"
        />
        <div className="text-[10px] text-purple-700">
          💡 Newlines → automatisch &lt;br&gt;. Wordt onderaan elke nieuwe mail toegevoegd (na &quot;-- &quot; scheiding).
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className="text-xs flex items-center gap-2"><input type="checkbox" checked={form.is_default} onChange={(e) => setForm(f => ({ ...f, is_default: e.target.checked }))} /> Standaard voor uitgaande mail</label>
        <label className="text-xs flex items-center gap-2"><input type="checkbox" checked={form.auto_route_facturen} onChange={(e) => setForm(f => ({ ...f, auto_route_facturen: e.target.checked }))} /> Auto-routeer factuur-PDFs naar Inkomende facturen</label>
      </div>
      <div>
        <label className="text-[10px] font-bold uppercase text-gray-500">Poll-interval (minuten, 1-60)</label>
        <input className="field-input mt-1" type="number" min="1" max="60" value={form.poll_interval_min} onChange={(e) => setForm(f => ({ ...f, poll_interval_min: parseInt(e.target.value) || 5 }))} />
      </div>
      {testResult && !testResult.ok && (
        <div className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700 inline-flex items-start gap-1.5">
          <WarningCircle size={14} className="flex-shrink-0 mt-0.5" /> {testResult.error}
        </div>
      )}
      <div className="flex items-center justify-end gap-2 pt-3 border-t">
        <button onClick={onCancel} className="btn btn-secondary btn-sm">Annuleer</button>
        <button onClick={doSave} disabled={saving} className="btn btn-primary btn-sm" data-testid="mailbox-form-save">
          {saving ? '⏳ Verbinden + opslaan...' : (editing ? 'Bijwerken' : 'Verbinden + opslaan')}
        </button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// FOLDER MODAL — custom mappen beheren
// ════════════════════════════════════════════════════════════════
function FolderModal({ folders, onClose, onChange }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ label: '', icon: '📁', color: '#8B5CF6', ai_auto_rule: '' });
  const [busy, setBusy] = useState(false);
  const ICON_PRESETS = ['📁', '🔥', '⭐', '💼', '📋', '🎯', '💡', '🚨', '📦', '🧾', '🤝', '📞', '✉️', '🏷️', '🗂️'];

  const startNew = () => { setEditing(null); setForm({ label: '', icon: '📁', color: '#8B5CF6', ai_auto_rule: '' }); setShowForm(true); };
  const startEdit = (f) => { setEditing(f.id); setForm({ label: f.label, icon: f.icon, color: f.color, ai_auto_rule: f.ai_auto_rule || '' }); setShowForm(true); };

  const save = async () => {
    if (!form.label.trim()) { alert('Label is verplicht'); return; }
    setBusy(true);
    try {
      if (editing) await API.put(`/mail/folders/${editing}`, form);
      else await API.post('/mail/folders', form);
      setShowForm(false);
      onChange();
    } catch (e) {
      alert('Opslaan mislukt: ' + (e.response?.data?.detail || e.message));
    }
    setBusy(false);
  };
  const del = async (fid) => {
    if (!confirm('Map verwijderen? Mails in deze map gaan terug naar INBOX.')) return;
    try { await API.delete(`/mail/folders/${fid}`); onChange(); }
    catch (e) { alert('Verwijderen mislukt: ' + (e.response?.data?.detail || e.message)); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="folder-modal">
      <div className="bg-white rounded-lg shadow-2xl max-w-xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b flex items-center justify-between bg-gray-50">
          <h3 className="text-lg font-bold text-gray-900">📁 Mappen beheren</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-200 rounded"><X size={18} /></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1">
          {!showForm && (
            <>
              {folders.length === 0 ? (
                <div className="text-center py-8 text-sm text-gray-500">
                  <div className="text-3xl mb-2">📁</div>
                  Nog geen eigen mappen. Klik hieronder om er één aan te maken.
                </div>
              ) : (
                <div className="space-y-1.5 mb-4">
                  {folders.map(f => (
                    <div key={f.id} className="flex items-center justify-between p-2.5 border border-gray-200 rounded hover:bg-gray-50">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span style={{ fontSize: 20 }}>{f.icon || '📁'}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900 truncate" style={{ color: f.color }}>{f.label}</div>
                          <div className="text-[10px] text-gray-500">
                            {f.count || 0} mail{(f.count || 0) === 1 ? '' : 's'}{f.unread > 0 && ` • ${f.unread} ongelezen`}
                            {f.ai_auto_rule && <span className="ml-1 text-purple-600">🤖 auto-regel actief</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => startEdit(f)} className="px-2 py-1 text-[10px] bg-gray-100 hover:bg-gray-200 rounded">Bewerk</button>
                        <button onClick={() => del(f.id)} className="px-2 py-1 text-[10px] bg-red-100 hover:bg-red-200 text-red-700 rounded">Verwijder</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <button onClick={startNew} className="w-full px-4 py-3 bg-[#8B5CF6] hover:bg-[#7C3AED] text-white rounded font-semibold inline-flex items-center justify-center gap-2" data-testid="folder-add-new">
                <Plus size={16} weight="bold" /> Nieuwe map aanmaken
              </button>
            </>
          )}
          {showForm && (
            <div className="space-y-3">
              <h4 className="text-sm font-bold text-gray-700">{editing ? 'Map bewerken' : 'Nieuwe map'}</h4>
              <div>
                <label className="text-[10px] font-bold uppercase text-gray-500">Naam</label>
                <input className="field-input mt-1" value={form.label} onChange={(e) => setForm(f => ({ ...f, label: e.target.value }))} placeholder="Bv. Belangrijke klanten, Te factureren..." data-testid="folder-form-label" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase text-gray-500">Pictogram</label>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {ICON_PRESETS.map(emoji => (
                    <button
                      key={emoji}
                      onClick={() => setForm(f => ({ ...f, icon: emoji }))}
                      className={`text-xl w-8 h-8 rounded border ${form.icon === emoji ? 'bg-[#8B5CF6] border-[#8B5CF6]' : 'bg-white border-gray-200 hover:border-gray-300'}`}
                    >{emoji}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase text-gray-500">Kleur</label>
                <input type="color" value={form.color} onChange={(e) => setForm(f => ({ ...f, color: e.target.value }))} className="mt-1 w-20 h-8 border border-gray-300 rounded cursor-pointer" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase text-gray-500">🤖 Auto-regel (optioneel)</label>
                <textarea
                  className="field-input mt-1 text-xs"
                  rows={2}
                  value={form.ai_auto_rule}
                  onChange={(e) => setForm(f => ({ ...f, ai_auto_rule: e.target.value }))}
                  placeholder="Bv. 'Alle mails van leveranciers met PDF-bijlage' (AI gebruikt dit om mails automatisch hier in te delen)"
                />
                <div className="text-[10px] text-gray-400 mt-0.5">💡 Optioneel: beschrijf in natuurlijke taal welke mails hier moeten landen.</div>
              </div>
              <div className="flex items-center justify-end gap-2 pt-3 border-t">
                <button onClick={() => setShowForm(false)} className="btn btn-secondary btn-sm">Annuleer</button>
                <button onClick={save} disabled={busy} className="btn btn-primary btn-sm" data-testid="folder-form-save">{busy ? '⏳' : 'Opslaan'}</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// DIGEST MODAL — AI Secretary dagelijkse briefing
// ════════════════════════════════════════════════════════════════
function DigestModal({ data, busy, onClose, onOpenMail, onRefresh }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="digest-modal">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b flex items-center justify-between bg-gradient-to-r from-purple-100 to-pink-100">
          <h3 className="text-lg font-bold text-gray-900 inline-flex items-center gap-2">
            <Sparkle size={20} weight="fill" className="text-purple-600" />
            AI Secretary — Dagelijkse briefing
          </h3>
          <div className="flex items-center gap-2">
            <button onClick={onRefresh} disabled={busy} className="text-xs px-2 py-1 bg-white hover:bg-gray-100 border rounded disabled:opacity-50">🔄 Ververs</button>
            <button onClick={onClose} className="p-1 hover:bg-white/50 rounded"><X size={18} /></button>
          </div>
        </div>
        <div className="p-5 overflow-y-auto flex-1">
          {busy && !data && <div className="text-center py-12 text-gray-500"><Sparkle size={32} weight="fill" className="mx-auto mb-2 text-purple-400 animate-pulse" /><div>AI analyseert je inbox...</div></div>}
          {data && (
            <div className="space-y-4">
              {/* Snel-overzicht */}
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="p-3 bg-blue-50 rounded">
                  <div className="text-2xl font-bold text-blue-700">{data.count || 0}</div>
                  <div className="text-[10px] text-blue-600">Mails vandaag</div>
                </div>
                <div className="p-3 bg-red-50 rounded">
                  <div className="text-2xl font-bold text-red-700">{data.urgent_count || 0}</div>
                  <div className="text-[10px] text-red-600">🔴 Dringend</div>
                </div>
                <div className="p-3 bg-amber-50 rounded">
                  <div className="text-2xl font-bold text-amber-700">{data.action_count || 0}</div>
                  <div className="text-[10px] text-amber-600">Actie nodig</div>
                </div>
              </div>
              {/* Summary */}
              {data.summary && (
                <div className="p-3 bg-purple-50 border border-purple-200 rounded text-sm text-purple-900">
                  <div className="text-[10px] font-bold uppercase text-purple-700 mb-1">Samenvatting</div>
                  {data.summary}
                </div>
              )}
              {/* Top priorities */}
              {(data.top_priorities || []).length > 0 && (
                <div>
                  <div className="text-xs font-bold text-gray-700 mb-1.5">🎯 Topprioriteiten vandaag</div>
                  <ul className="space-y-1.5">
                    {data.top_priorities.map((p, i) => (
                      <li key={i} className="text-sm text-gray-800 flex items-start gap-2 p-2 bg-red-50/50 border border-red-100 rounded">
                        <span className="text-red-500 font-bold">{i + 1}.</span> {p}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {/* Highlights */}
              {(data.highlights || []).length > 0 && (
                <div>
                  <div className="text-xs font-bold text-gray-700 mb-1.5">📌 Belangrijke punten</div>
                  <ul className="space-y-1 text-xs text-gray-700">
                    {data.highlights.map((h, i) => <li key={i} className="px-2">• {h}</li>)}
                  </ul>
                </div>
              )}
              {/* Urgent mails */}
              {(data.urgent || []).length > 0 && (
                <div>
                  <div className="text-xs font-bold text-gray-700 mb-1.5">🔴 Dringende mails</div>
                  <div className="space-y-1.5">
                    {data.urgent.map((m, i) => (
                      <button
                        key={m.id || i}
                        onClick={() => onOpenMail(m.id)}
                        className="w-full text-left p-2 border border-red-200 rounded hover:bg-red-50 text-xs"
                      >
                        <div className="font-semibold text-gray-900 truncate">{m.subject}</div>
                        <div className="text-gray-500 truncate">{m.from} • {m.summary}</div>
                        {m.action && <div className="text-purple-700 italic mt-0.5">💡 {m.action}</div>}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}



// ══════════════════════════════════════════════════════════════
// 🌐 Community spam-banner — verschijnt boven de SPAM-folder
// ══════════════════════════════════════════════════════════════
function CommunitySpamBanner() {
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    API.get('/mail/spam/community-stats').then(r => setStats(r.data)).catch(() => {});
  }, []);

  const toggleOptout = async () => {
    if (!stats) return;
    setBusy(true);
    try {
      const r = await API.post('/mail/spam/community-optout', { optout: !stats.optout });
      const s2 = await API.get('/mail/spam/community-stats');
      setStats(s2.data);
      alert(r.data.optout
        ? '🔕 Je doet niet meer mee aan de community spam-lijst. Je eerdere bijdragen zijn verwijderd.'
        : '🌐 Je doet weer mee. Domein-blokkades worden anoniem gedeeld om alle BillFac-gebruikers te beschermen.');
    } catch (e) {
      alert('Wijziging mislukt: ' + (e.response?.data?.detail || e.message));
    }
    setBusy(false);
  };

  if (!stats) return null;
  return (
    <div className="p-2.5 border-b bg-gradient-to-r from-orange-50 to-red-50 flex-shrink-0" data-testid="community-spam-banner">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-1.5 mb-1">
            <span className="text-base">🌐</span>
            <span className="text-xs font-bold uppercase tracking-wider text-orange-700">Community spam-bescherming</span>
            {stats.optout && (
              <span className="text-[9px] font-bold uppercase bg-gray-400 text-white px-1.5 py-0.5 rounded">UITGESCHAKELD</span>
            )}
          </div>
          <div className="text-xs text-gray-700">
            {stats.optout ? (
              <>Je bent <strong>niet</strong> beschermd door de community spam-lijst. Schakel hem aan om automatisch beschermd te worden tegen spam-domeinen die andere BillFac-gebruikers hebben gemeld.</>
            ) : (
              <>
                <strong className="text-orange-700">{stats.total_protected_domains}</strong> spam-domeinen worden actief geblokkeerd door BillFac-gebruikers samen.
                {stats.my_contributions > 0 && (
                  <> Jij hebt <strong>{stats.my_contributions}</strong> {stats.my_contributions === 1 ? 'domein' : 'domeinen'} bijgedragen. ❤️</>
                )}
                <span className="block text-[10px] text-gray-500 mt-0.5">
                  🔒 100% anoniem — alleen SHA-256 hashes van domeinen, geen e-mailadressen, geen reverse-lookup mogelijk.
                </span>
              </>
            )}
          </div>
        </div>
        <button
          onClick={toggleOptout}
          disabled={busy}
          className={`shrink-0 px-2.5 py-1 text-[11px] rounded font-bold uppercase transition-colors ${stats.optout
            ? 'bg-orange-600 hover:bg-orange-700 text-white'
            : 'bg-white border border-orange-300 hover:bg-orange-100 text-orange-700'}`}
          data-testid="community-spam-toggle"
        >
          {busy ? '...' : stats.optout ? 'Aanzetten' : 'Opt-out'}
        </button>
      </div>
    </div>
  );
}

// ─── Microsoft 365 OAuth koppelknop (PREMIUM) ────────────────────────────────
function M365ConnectButton({ onChange }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await API.get('/mail/m365/status');
      setStatus(r.data);
    } catch (e) {
      setStatus({ configured: false, connected: false });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Listen for postMessage van OAuth callback (popup-flow)
  useEffect(() => {
    const handler = (ev) => {
      if (ev.data?.type === 'm365-connected') {
        setBusy(false);
        load();
        if (onChange) onChange();
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [load, onChange]);

  if (!status) return null;

  const handleConnect = async () => {
    if (!status.configured) {
      alert('Microsoft 365 OAuth is nog niet geconfigureerd op de server. Vraag de platform-beheerder om M365_CLIENT_ID, M365_CLIENT_SECRET en M365_REDIRECT_URI in te stellen in de backend .env.');
      return;
    }
    setBusy(true);
    try {
      const r = await API.get('/mail/m365/connect');
      const url = r.data?.url;
      if (!url) throw new Error('Geen authorisatie-URL ontvangen');
      // Open in nieuw venster (auto-redirect na auth)
      window.open(url, 'm365-oauth', 'width=600,height=700,left=200,top=100');
    } catch (e) {
      alert('Koppelen mislukt: ' + (e.response?.data?.detail || e.message));
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Microsoft 365 ontkoppelen? Bestaande mails blijven bewaard, maar nieuwe sync stopt.')) return;
    setBusy(true);
    try {
      await API.post('/mail/m365/disconnect');
      await load();
      if (onChange) onChange();
    } catch (e) {
      alert('Ontkoppelen mislukt: ' + (e.response?.data?.detail || e.message));
    }
    setBusy(false);
  };

  return (
    <div className="mt-3 p-3 border-2 border-dashed border-blue-300 bg-blue-50 rounded" data-testid="m365-connect-section">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold uppercase text-blue-700 bg-white border border-blue-300 px-1.5 py-0.5 rounded">PREMIUM</span>
          <span className="font-semibold text-blue-900 text-sm">Microsoft 365 / Outlook OAuth</span>
        </div>
        {status.connected && (
          <span className="text-[10px] bg-green-100 text-green-800 px-2 py-0.5 rounded font-bold">VERBONDEN</span>
        )}
      </div>
      <p className="text-xs text-blue-800 mt-1.5">
        Koppel je M365-mailbox veilig zonder app-wachtwoord. Lees & verstuur via Microsoft Graph API.
      </p>
      {status.connected ? (
        <div className="mt-2 flex items-center justify-between">
          <span className="text-xs text-blue-900">
            {status.account?.email}
            {status.account?.linked_at && <span className="ml-2 text-blue-500">(gekoppeld: {status.account.linked_at.slice(0, 10)})</span>}
          </span>
          <button
            onClick={handleDisconnect}
            disabled={busy}
            className="px-3 py-1 text-xs bg-white hover:bg-red-50 border border-red-300 text-red-700 rounded disabled:opacity-50"
            data-testid="m365-disconnect-btn"
          >
            Ontkoppel
          </button>
        </div>
      ) : (
        <button
          onClick={handleConnect}
          disabled={busy}
          className="mt-2 w-full px-3 py-2 bg-white hover:bg-blue-100 border-2 border-blue-500 text-blue-900 rounded font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="m365-connect-btn"
        >
          {busy ? 'Bezig...' : (
            <>
              <svg width="16" height="16" viewBox="0 0 23 23" xmlns="http://www.w3.org/2000/svg">
                <rect x="1" y="1" width="10" height="10" fill="#F25022"/>
                <rect x="12" y="1" width="10" height="10" fill="#7FBA00"/>
                <rect x="1" y="12" width="10" height="10" fill="#00A4EF"/>
                <rect x="12" y="12" width="10" height="10" fill="#FFB900"/>
              </svg>
              {status.configured ? 'Koppel Microsoft 365 (OAuth)' : 'M365 niet geconfigureerd op server'}
            </>
          )}
        </button>
      )}
    </div>
  );
}


// ─── Template Dropdown — Portal-based, voorkomt clipping in scroll-containers ─
function TemplateDropdown({ templates, templateFilter, setTemplateFilter, show, setShow, applyTemplate, seedTemplates }) {
  const btnRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 320 });

  // Bereken positie t.o.v. button (in fixed coords)
  const updatePos = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const DROPDOWN_W = 360; // iets ruimer dan w-80 (320)
    const VPW = window.innerWidth;
    // Right-align onder de knop, maar clamp binnen viewport (8px margin)
    let left = rect.right - DROPDOWN_W;
    if (left < 8) left = 8;
    if (left + DROPDOWN_W > VPW - 8) left = VPW - DROPDOWN_W - 8;
    const top = rect.bottom + 4;
    setPos({ top, left, width: DROPDOWN_W });
  }, []);

  useEffect(() => {
    if (!show) return;
    updatePos();
    const handler = () => updatePos();
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true); // capture: ook bij scroll van scroll-parents
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
    };
  }, [show, updatePos]);

  // Klik buiten → sluiten
  useEffect(() => {
    if (!show) return;
    const handler = (ev) => {
      if (btnRef.current?.contains(ev.target)) return;
      // Detect click in portal-dropdown via data-attribute
      const portalEl = ev.target.closest('[data-testid="mail-compose-templates-list"]');
      if (portalEl) return;
      setShow(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [show, setShow]);

  const filtered = templates.filter(t =>
    !templateFilter || (t.name + ' ' + t.category).toLowerCase().includes(templateFilter.toLowerCase())
  );

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setShow(s => !s)}
        className="text-[10px] px-2 py-1 bg-white hover:bg-amber-50 border border-amber-300 text-amber-700 rounded inline-flex items-center gap-1"
        title="Kies een mail-sjabloon"
        data-testid="mail-compose-templates-btn"
      >
        📑 Sjabloon{templates.length > 0 ? ` (${templates.length})` : ''}
      </button>
      {show && createPortal(
        <div
          className="fixed z-[9999] bg-white border border-gray-200 shadow-2xl max-h-96 overflow-y-auto rounded"
          style={{ top: pos.top, left: pos.left, width: pos.width }}
          data-testid="mail-compose-templates-list"
        >
          <div className="px-3 py-2 border-b sticky top-0 bg-white flex items-center gap-2">
            <input
              type="text"
              value={templateFilter}
              onChange={e => setTemplateFilter(e.target.value)}
              placeholder="Zoek sjabloon..."
              className="flex-1 text-xs px-2 py-1 border border-gray-200 rounded"
              autoFocus
            />
            <button onClick={() => setShow(false)} className="text-gray-400 hover:text-gray-700" title="Sluiten"><X size={14} /></button>
          </div>
          {templates.length === 0 ? (
            <div className="p-4 text-center text-xs text-gray-500">
              <p className="mb-2">Nog geen sjablonen.</p>
              <button onClick={seedTemplates} className="text-[10px] px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded font-bold">
                ✨ Voorbeeld-sjablonen aanmaken
              </button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-4 text-center text-xs text-gray-400">Geen sjablonen gevonden voor "{templateFilter}"</div>
          ) : (
            filtered.map(t => (
              <button
                key={t.id}
                onClick={() => { applyTemplate(t); }}
                className="w-full text-left px-3 py-2 hover:bg-amber-50 border-b border-gray-100 last:border-b-0"
                data-testid={`mail-compose-template-${t.id}`}
              >
                <div className="text-xs font-bold text-gray-900 truncate">{t.name}</div>
                <div className="text-[10px] text-gray-500 flex items-center gap-2 mt-0.5">
                  <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">{t.category}</span>
                  <span>{t.lang?.toUpperCase()}</span>
                  {t.subject && <span className="truncate flex-1 min-w-0">— {t.subject}</span>}
                </div>
              </button>
            ))
          )}
        </div>,
        document.body
      )}
    </div>
  );
}


// ─── Smart Quick-Replies (3 AI-zinnen, 1-klik antwoord) ────────────────────
function QuickReplies({ msg, onPick }) {
  const [loading, setLoading] = useState(false);
  const [replies, setReplies] = useState([]);
  const [loaded, setLoaded] = useState(false);

  // Triggers automatisch bij open van inkomende mail
  useEffect(() => {
    const isInbound = msg?.direction === 'inbound' || msg?.direction === 'in';
    if (!msg?.id || !isInbound) { setReplies([]); setLoaded(false); return; }
    let cancelled = false;
    setLoading(true);
    setLoaded(false);
    API.post(`/mail/ai/quick-replies/${msg.id}`)
      .then(r => { if (!cancelled) { setReplies(r.data?.replies || []); setLoaded(true); } })
      .catch(() => { if (!cancelled) { setReplies([]); setLoaded(true); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [msg?.id, msg?.direction]);

  const isInbound = msg?.direction === 'inbound' || msg?.direction === 'in';
  if (!isInbound) return null;
  if (loading) {
    return (
      <div className="mt-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded text-xs text-emerald-800 flex items-center gap-2" data-testid="quick-replies-loading">
        <Sparkle size={12} weight="fill" className="animate-pulse" />
        AI denkt na over snelle antwoorden...
      </div>
    );
  }
  if (!loaded || replies.length === 0) return null;
  return (
    <div className="mt-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded text-xs" data-testid="quick-replies-bar">
      <div className="flex items-center gap-2 mb-1.5">
        <Sparkle size={12} weight="fill" className="text-emerald-600" />
        <strong className="text-emerald-900">Snel antwoorden:</strong>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {replies.map((txt, i) => (
          <button
            key={i}
            onClick={() => onPick(txt)}
            className="px-2.5 py-1 bg-white hover:bg-emerald-100 border border-emerald-300 text-emerald-900 rounded-full text-xs transition-colors"
            title={txt}
            data-testid={`quick-reply-${i}`}
          >
            {txt.length > 60 ? txt.slice(0, 57) + '...' : txt}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Klant-context paneel (BillFac UNIEK!) ──────────────────────────────────
function CustomerContextPanel({ msg }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    const isInbound = msg?.direction === 'inbound' || msg?.direction === 'in';
    if (!msg?.id || !isInbound) { setData(null); return; }
    let cancelled = false;
    setLoading(true);
    API.get(`/mail/customer-context/${msg.id}`)
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [msg?.id, msg?.direction]);

  const isInbound = msg?.direction === 'inbound' || msg?.direction === 'in';
  if (!isInbound) return null;
  if (loading) {
    return (
      <div className="mt-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700" data-testid="customer-context-loading">
        🔎 Klant zoeken...
      </div>
    );
  }
  if (!data) return null;
  if (!data.matched) {
    return (
      <div className="mt-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded text-xs text-gray-500 flex items-center justify-between" data-testid="customer-context-nomatch">
        <span>👤 Onbekende afzender — geen klant gevonden voor <code className="text-gray-700">{data.from_email}</code></span>
        <button
          onClick={() => window.open(`/klanten?new=1&email=${encodeURIComponent(data.from_email || '')}`, '_blank')}
          className="ml-2 px-2 py-0.5 bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-100"
        >+ Klant aanmaken</button>
      </div>
    );
  }
  const k = data.klant;
  const kpis = data.kpis || {};
  const eur = (v) => `€${(Number(v) || 0).toLocaleString('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (
    <div className="mt-2 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-300 rounded overflow-hidden" data-testid="customer-context-panel">
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full px-3 py-2 flex items-center justify-between hover:bg-blue-100 transition-colors text-left"
        data-testid="customer-context-toggle"
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span className="text-blue-700 text-base">🏢</span>
          <strong className="text-blue-900 text-sm truncate">{k.naam}</strong>
          {k.prijsgroep && <span className="text-[10px] bg-blue-200 text-blue-900 px-1.5 py-0.5 rounded">{k.prijsgroep}</span>}
        </div>
        <div className="flex items-center gap-3 text-xs">
          {kpis.open_facturen_aantal > 0 ? (
            <span className="font-bold text-red-700">
              {kpis.open_facturen_aantal}× open · {eur(kpis.open_bedrag)}
            </span>
          ) : (
            <span className="text-green-700 font-bold">✓ Geen openstaande</span>
          )}
          <span className="text-gray-600">YTD: <strong className="text-blue-900">{eur(kpis.omzet_ytd)}</strong></span>
          <span className="text-blue-600">{expanded ? '▾' : '▸'}</span>
        </div>
      </button>
      {expanded && (
        <div className="px-3 pb-3 pt-1 border-t border-blue-200 grid grid-cols-3 gap-3 text-xs" data-testid="customer-context-expanded">
          {/* Klant-info */}
          <div className="space-y-0.5">
            <div className="text-[10px] uppercase font-bold text-blue-600 tracking-wider">Contact</div>
            {k.email && <div className="truncate"><span className="text-gray-500">✉</span> {k.email}</div>}
            {k.telefoon && <div className="truncate"><span className="text-gray-500">☎</span> <a href={`tel:${k.telefoon}`} className="text-blue-700 hover:underline">{k.telefoon}</a></div>}
            {k.btw_nr && <div className="truncate text-gray-600">BTW: {k.btw_nr}</div>}
            {k.adres && <div className="truncate text-gray-600">{k.adres}</div>}
            <a href={`/klanten/${k.id}`} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline font-semibold mt-1 inline-block">→ Open klant-fiche</a>
          </div>
          {/* Open facturen */}
          <div className="space-y-0.5">
            <div className="text-[10px] uppercase font-bold text-red-600 tracking-wider">
              Open ({kpis.open_facturen_aantal})
            </div>
            {(data.open_facturen || []).slice(0, 4).map(f => (
              <div key={f.id} className="flex justify-between gap-1 text-[11px] hover:bg-blue-100 rounded px-1 py-0.5">
                <a href={`/facturen/${f.id}`} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline truncate">{f.nummer}</a>
                <span className="font-bold text-red-700 whitespace-nowrap">{eur(f.totaal_incl)}</span>
              </div>
            ))}
            {(data.open_facturen || []).length === 0 && <div className="text-green-700 text-[11px]">Geen openstaande facturen ✓</div>}
          </div>
          {/* Laatste activiteit */}
          <div className="space-y-0.5">
            <div className="text-[10px] uppercase font-bold text-purple-600 tracking-wider">Laatste docs</div>
            {(data.laatste_docs || []).slice(0, 4).map(d => (
              <div key={d.id} className="flex justify-between gap-1 text-[11px] hover:bg-blue-100 rounded px-1 py-0.5">
                <a href={`/facturen/${d.id}`} target="_blank" rel="noopener noreferrer" className="text-purple-700 hover:underline truncate" title={d.doc_type}>
                  {d.nummer}
                </a>
                <span className="text-gray-600 whitespace-nowrap">{(d.datum || '').slice(0, 10)}</span>
              </div>
            ))}
            {(data.laatste_docs || []).length === 0 && <div className="text-gray-500 italic text-[11px]">Nog geen documenten</div>}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── AI Inbox-Triage knop (batch-classificatie) ─────────────────────────────
function AiTriageButton({ onDone }) {
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const run = async () => {
    setBusy(true);
    setLastResult(null);
    try {
      const r = await API.post('/mail/ai/triage', { limit: 25 });
      setLastResult(r.data);
      if (r.data?.processed > 0 && onDone) onDone();
    } catch (e) {
      alert('AI triage mislukt: ' + (e.response?.data?.detail || e.message));
    }
    setBusy(false);
    // Auto-hide toast na 4s
    setTimeout(() => setLastResult(null), 4500);
  };

  return (
    <div className="relative">
      <button
        onClick={run}
        disabled={busy}
        className="px-3 py-2 bg-gradient-to-r from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700 text-white rounded text-sm inline-flex items-center gap-1.5 disabled:opacity-50 shadow-sm"
        title="AI sorteert je inbox: dringend / klant / leverancier / nieuwsbrief / spam"
        data-testid="ai-triage-btn"
      >
        <Sparkle size={14} weight="fill" className={busy ? 'animate-pulse' : ''} />
        {busy ? 'AI sorteert...' : 'AI triage'}
      </button>
      {lastResult && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-purple-300 shadow-lg rounded px-3 py-2 text-xs z-50 min-w-[260px]" data-testid="triage-toast">
          <strong className="text-purple-900">✨ {lastResult.processed} mails gesorteerd</strong>
          {lastResult.processed === 0 && <div className="text-gray-500 mt-0.5">Geen ongesorteerde inbox-mails gevonden.</div>}
          {(lastResult.results || []).slice(0, 4).map(r => (
            <div key={r.id} className="mt-1 text-[11px] text-gray-700 truncate">
              <span className={`inline-block px-1 rounded text-[10px] font-bold mr-1 ${
                r.ai_urgency === 'high' ? 'bg-red-100 text-red-700' :
                r.ai_urgency === 'medium' ? 'bg-amber-100 text-amber-700' :
                'bg-gray-100 text-gray-600'
              }`}>{r.ai_category}</span>
              {r.subject}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Undo-Send Toast (5 sec annuleer-venster) ──────────────────────────────
function UndoSendToast({ pending, onCancel, onComplete }) {
  // pending: { token, secondsLeft, subject } | null
  const [secondsLeft, setSecondsLeft] = useState(pending?.secondsLeft || 5);
  useEffect(() => {
    if (!pending) return;
    setSecondsLeft(pending.secondsLeft || 5);
    let cancelled = false;
    const interval = setInterval(() => {
      if (cancelled) return;
      setSecondsLeft(s => {
        const next = s - 1;
        if (next <= 0) {
          clearInterval(interval);
          if (onComplete) onComplete(pending.token);
          return 0;
        }
        return next;
      });
    }, 1000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [pending, onComplete]);
  if (!pending) return null;
  return (
    <div
      className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white rounded-lg shadow-2xl px-4 py-3 flex items-center gap-3 z-[9999] min-w-[320px]"
      data-testid="undo-send-toast"
    >
      <div className="relative w-8 h-8 flex-shrink-0">
        <svg className="w-8 h-8 -rotate-90" viewBox="0 0 32 32">
          <circle cx="16" cy="16" r="14" fill="none" stroke="#374151" strokeWidth="3" />
          <circle cx="16" cy="16" r="14" fill="none" stroke="#10B981" strokeWidth="3"
            strokeDasharray={`${(secondsLeft / 5) * 87.96} 87.96`} strokeLinecap="round" />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold">{secondsLeft}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold">Mail verstuurd</div>
        <div className="text-xs text-gray-400 truncate">{pending.subject}</div>
      </div>
      <button
        onClick={() => onCancel(pending.token)}
        className="px-3 py-1.5 bg-red-600 hover:bg-red-700 rounded text-sm font-bold transition-colors"
        data-testid="undo-send-btn"
      >
        ↶ Ongedaan maken
      </button>
    </div>
  );
}


